// Admin Portal
// src/pages/LMS/CourseBuilder.jsx
// Enterprise-Grade Course Builder - Light Theme Edition with Supporting Documents
import React, { useState, useEffect, useCallback } from 'react';
// Tiptap Imports
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapTable from "@tiptap/extension-table";
import TiptapTableCell from "@tiptap/extension-table-cell";
import TiptapTableHeader from "@tiptap/extension-table-header";
import TiptapTableRow from "@tiptap/extension-table-row";
import TiptapLink from "@tiptap/extension-link";  // FIXED: Remove curly braces
import Underline from "@tiptap/extension-underline";
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';

// Import your TiptapEditor component (same as EditDocuments uses)
import TiptapEditor from "../../components/Common/TiptapEditor";
import { Document, Packer, Paragraph, TextRun } from 'docx';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent,
  TextField, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem,
  Divider, List, ListItem, ListItemText, ListItemIcon, Chip, Stack,
  CircularProgress, Switch, FormControlLabel, Tooltip, Avatar, Menu,
  LinearProgress, Fade, Slide, Collapse, Badge, alpha, Snackbar,
  Alert as MuiAlert, Breadcrumbs, Link, InputAdornment, Skeleton,
  ToggleButton, ToggleButtonGroup, useTheme, Tab, Tabs,

} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Save as SaveIcon,
  PlayArrow as PublishIcon,
  ArrowBack as BackIcon,
  VideoLibrary as VideoIcon,
  PictureAsPdf as PdfIcon,
  Article as DocumentIcon,
  Quiz as TestIcon,
  CloudUpload as UploadIcon,
  Settings as SettingsIcon,
  School as ModuleIcon,
  MenuBook as SubmoduleIcon,
  Subtitles as TranscriptIcon,
  CheckCircle as PublishedIcon,
  Drafts as DraftIcon,
  KeyboardArrowRight as ArrowRightIcon,
  MoreVert as MoreIcon,
  ContentCopy as DuplicateIcon,
  Visibility as PreviewIcon,
  Timer as TimerIcon,
  People as StudentsIcon,
  TrendingUp as AnalyticsIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  AutoAwesome as AIIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  GridView as GridViewIcon,
  ViewList as ListViewIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Archive as ArchiveIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Help as HelpIcon,
  Keyboard as ShortcutsIcon,
  NavigateNext as BreadcrumbIcon,
  Psychology as TrainIcon,
  ModelTraining as TrainingIcon,
  RemoveCircleOutline as UntrainIcon,
  Sync as SyncIcon,
  ErrorOutline as ErrorIcon,
  CheckCircleOutline as SuccessIcon,
  Schedule as PendingIcon,
  Refresh as RefreshIcon,
  TextSnippet as ContentIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
  LibraryBooks as LibraryBooksIcon,
  Policy as PolicyIcon,
  Assignment as AssignmentIcon,
  FileCopy as FileCopyIcon,
  VisibilityOff as VisibilityOffIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  RecordVoiceOver as VoiceIcon,
  GraphicEq as AudioIcon,
  Campaign as AnnouncementIcon,
  VolumeUp as SpeakerIcon,
  VolumeOff as MuteIcon,
  PlayCircle as PlayIcon,
  StopCircle as StopIcon,
  Mic as MicIcon,
  Person as PersonIcon,
  Female as FemaleIcon,
  Male as MaleIcon,
  Language as LanguageIcon,
  Celebration as CelebrationIcon,
  EmojiEvents as TrophyIcon,
  School as GraduationIcon,
  SportsScore as ScoreIcon,
  Lightbulb as TipIcon,
  SmartToy as RobotIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlinedIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as NumberListIcon,
  FormatAlignLeft as FormatAlignLeftIcon,
  FormatAlignCenter as FormatAlignCenterIcon,
  FormatAlignRight as FormatAlignRightIcon,
  FormatQuote as QuoteIcon,
  Code as CodeIcon,
  Title as HeadingIcon,
  Create as CreateIcon,
  TextSnippet as TextEditorIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axiosInstance from '../../utils/axiosInstance';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FILE_TYPE_MAPPINGS = {
  // Video extensions
  video: ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv', '.wmv', '.m4v', '.mpeg', '.mpg', '.3gp'],
  // Slide/Presentation extensions
  slide: ['.pdf', '.ppt', '.pptx', '.odp', '.key'],
  // Document extensions
  document: ['.doc', '.docx', '.txt', '.md', '.rtf', '.odt', '.html', '.htm', '.xml', '.json', '.csv', '.xls', '.xlsx'],
};

const getContentTypeFromExtension = (filename) => {
  if (!filename) return null;
  const extension = '.' + filename.split('.').pop().toLowerCase();
  
  for (const [contentType, extensions] of Object.entries(FILE_TYPE_MAPPINGS)) {
    if (extensions.includes(extension)) {
      return contentType;
    }
  }
  return null;
};

const getAcceptedExtensions = (contentType) => {
  switch (contentType) {
    case 'video':
      return FILE_TYPE_MAPPINGS.video.join(',');
    case 'slide':
      return FILE_TYPE_MAPPINGS.slide.join(',');
    case 'document':
      return FILE_TYPE_MAPPINGS.document.join(',');
    default:
      return '*';
  }
};

const getContentTypeLabel = (contentType) => {
  switch (contentType) {
    case 'video':
      return 'Video';
    case 'slide':
      return 'Slide/PDF';
    case 'document':
      return 'Document';
    default:
      return contentType;
  }
};

// ============================================================================
// VIDEO URL HELPERS
// ============================================================================
const VIDEO_PROVIDERS = {
  YOUTUBE: 'youtube',
  VIMEO: 'vimeo',
  DAILYMOTION: 'dailymotion',
  WISTIA: 'wistia',
  LOOM: 'loom',
  CUSTOM: 'custom'
};

const detectVideoProvider = (url) => {
  if (!url) return null;
  const urlLower = url.toLowerCase();
  if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) return VIDEO_PROVIDERS.YOUTUBE;
  if (urlLower.includes('vimeo.com')) return VIDEO_PROVIDERS.VIMEO;
  if (urlLower.includes('dailymotion.com') || urlLower.includes('dai.ly')) return VIDEO_PROVIDERS.DAILYMOTION;
  if (urlLower.includes('wistia.com') || urlLower.includes('wistia.net')) return VIDEO_PROVIDERS.WISTIA;
  if (urlLower.includes('loom.com')) return VIDEO_PROVIDERS.LOOM;
  return VIDEO_PROVIDERS.CUSTOM;
};

const extractVideoId = (url, provider) => {
  if (!url) return null;
  try {
    switch (provider) {
      case VIDEO_PROVIDERS.YOUTUBE: {
        const patterns = [
          /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
          /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
        ];
        for (const pattern of patterns) {
          const match = url.match(pattern);
          if (match) return match[1];
        }
        break;
      }
      case VIDEO_PROVIDERS.VIMEO: {
        const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
        return match ? match[1] : null;
      }
      case VIDEO_PROVIDERS.DAILYMOTION: {
        const patterns = [/dailymotion\.com\/video\/([a-zA-Z0-9]+)/, /dai\.ly\/([a-zA-Z0-9]+)/];
        for (const pattern of patterns) {
          const match = url.match(pattern);
          if (match) return match[1];
        }
        break;
      }
      case VIDEO_PROVIDERS.WISTIA: {
        const match = url.match(/wistia\.(?:com|net)\/(?:medias|embed)\/([a-zA-Z0-9]+)/);
        return match ? match[1] : null;
      }
      case VIDEO_PROVIDERS.LOOM: {
        const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
        return match ? match[1] : null;
      }
      default:
        return null;
    }
  } catch (error) {
    return null;
  }
  return null;
};

const generateEmbedUrl = (url) => {
  const provider = detectVideoProvider(url);
  const videoId = extractVideoId(url, provider);
  if (!videoId) return url;
  
  switch (provider) {
    case VIDEO_PROVIDERS.YOUTUBE: return `https://www.youtube.com/embed/${videoId}`;
    case VIDEO_PROVIDERS.VIMEO: return `https://player.vimeo.com/video/${videoId}`;
    case VIDEO_PROVIDERS.DAILYMOTION: return `https://www.dailymotion.com/embed/video/${videoId}`;
    case VIDEO_PROVIDERS.WISTIA: return `https://fast.wistia.net/embed/iframe/${videoId}`;
    case VIDEO_PROVIDERS.LOOM: return `https://www.loom.com/embed/${videoId}`;
    default: return url;
  }
};

const generateEmbedCode = (url) => {
  const embedUrl = generateEmbedUrl(url);
  if (!embedUrl) return null;
  return `<iframe src="${embedUrl}" width="100%" height="400" frameborder="0" allowfullscreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"></iframe>`;
};

const parseVideoUrl = (url) => {
  if (!url) return null;
  const provider = detectVideoProvider(url);
  const videoId = extractVideoId(url, provider);
  const embedUrl = generateEmbedUrl(url);
  const embedCode = generateEmbedCode(url);
  const isValid = !!videoId || provider === VIDEO_PROVIDERS.CUSTOM;
  return { originalUrl: url, provider, videoId, embedUrl, embedCode, isValid };
};

// ============================================================================
// STYLED COMPONENTS
// ============================================================================
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#f8fafc',
  backgroundImage: `
    radial-gradient(ellipse at 0% 0%, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
    radial-gradient(ellipse at 100% 100%, rgba(236, 72, 153, 0.04) 0%, transparent 50%),
    linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)
  `,
  color: '#1e293b',
  fontFamily: '"Inter", "SF Pro Display", -apple-system, sans-serif',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  padding: '20px 32px',
  borderBottom: '1px solid #e2e8f0',
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px)',
  position: 'sticky',
  top: 50,
  zIndex: 100,
}));

const ContentArea = styled(Box)(({ theme }) => ({
  padding: '32px',
  maxWidth: '1600px',
  margin: '0 auto',
}));

const GlassCard = styled(Paper)(({ theme, variant }) => ({
  background: '#ffffff',
  borderRadius: '16px',
  border: '1px solid #e2e8f0',
  boxShadow: variant === 'elevated' 
    ? '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.05)'
    : '0 1px 3px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  '&:hover': {
    border: '1px solid #cbd5e1',
    transform: variant === 'elevated' ? 'translateY(-2px)' : 'none',
    boxShadow: variant === 'elevated' 
      ? '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 20px 40px -10px rgba(0, 0, 0, 0.04)'
      : '0 2px 4px rgba(0, 0, 0, 0.06)',
  },
}));

const ModuleCard = styled(GlassCard)(({ theme, isExpanded, status }) => ({
  marginBottom: '16px',
  border: isExpanded ? '1px solid #a5b4fc' : '1px solid #e2e8f0',
  position: 'relative',
  background: '#ffffff',
  '&::before': status === 'published' ? {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
    borderRadius: '16px 16px 0 0',
  } : {},
}));

const SubmoduleCard = styled(Box)(({ theme }) => ({
  background: '#f8fafc',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  padding: '16px',
  marginBottom: '12px',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#f1f5f9',
    border: '1px solid #cbd5e1',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
  },
}));

const ContentItem = styled(ListItem)(({ theme, isTrained }) => ({
  background: '#ffffff',
  borderRadius: '10px',
  marginBottom: '8px',
  border: isTrained ? '1px solid #a7f3d0' : '1px solid #e2e8f0',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#f8fafc',
    border: isTrained ? '1px solid #6ee7b7' : '1px solid #cbd5e1',
  },
}));

const SupportingDocumentItem = styled(Box)(({ theme, isTrained, isVisible }) => ({
  background: isVisible ? '#ffffff' : '#f8fafc',
  borderRadius: '12px',
  marginBottom: '12px',
  border: isTrained ? '1px solid #a7f3d0' : '1px solid #e2e8f0',
  padding: '16px',
  transition: 'all 0.2s ease',
  opacity: isVisible ? 1 : 0.7,
  '&:hover': {
    background: '#f8fafc',
    border: isTrained ? '1px solid #6ee7b7' : '1px solid #cbd5e1',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  },
}));

const TrainingButton = styled(Button)(({ theme, trained }) => ({
  background: trained 
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  color: '#fff',
  borderRadius: '8px',
  padding: '6px 12px',
  fontWeight: 600,
  fontSize: '12px',
  textTransform: 'none',
  boxShadow: trained 
    ? '0 2px 8px rgba(16, 185, 129, 0.25)'
    : '0 2px 8px rgba(99, 102, 241, 0.25)',
  transition: 'all 0.2s ease',
  minWidth: 'auto',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: trained 
      ? '0 4px 12px rgba(16, 185, 129, 0.35)'
      : '0 4px 12px rgba(99, 102, 241, 0.35)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
}));

const TrainingStatusChip = styled(Chip)(({ theme, status }) => {
  const colors = {
    trained: { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
    scraped: { bg: '#eef2ff', text: '#4f46e5', border: '#c7d2fe' },
    pending: { bg: '#fefce8', text: '#ca8a04', border: '#fde047' },
    error: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
  };
  const color = colors[status] || colors.pending;
  return {
    backgroundColor: color.bg,
    color: color.text,
    border: `1px solid ${color.border}`,
    fontWeight: 500,
    fontSize: '10px',
    height: '22px',
    borderRadius: '6px',
    '& .MuiChip-icon': { color: color.text, fontSize: '14px' },
  };
});

const TrainingInfoBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
  border: '1px solid #bbf7d0',
  borderRadius: '12px',
  padding: '16px',
  marginTop: '12px',
}));

const SpinningIcon = styled(SyncIcon)(({ theme }) => ({
  animation: `${spin} 1s linear infinite`,
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
  color: '#fff',
  borderRadius: '10px',
  padding: '10px 24px',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  letterSpacing: '0.2px',
  boxShadow: '0 4px 14px rgba(79, 70, 229, 0.25)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #4338ca 0%, #5558e3 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(79, 70, 229, 0.35)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  background: '#ffffff',
  color: '#475569',
  borderRadius: '10px',
  padding: '10px 24px',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#f8fafc',
    border: '1px solid #cbd5e1',
    color: '#1e293b',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
  },
}));

const SuccessButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  color: '#fff',
  borderRadius: '10px',
  padding: '10px 24px',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  boxShadow: '0 4px 14px rgba(16, 185, 129, 0.25)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(16, 185, 129, 0.35)',
  },
}));

const OutlineButton = styled(Button)(({ theme, color }) => ({
  background: 'transparent',
  color: color === 'success' ? '#059669' : color === 'error' ? '#dc2626' : '#475569',
  borderRadius: '8px',
  padding: '8px 16px',
  fontWeight: 500,
  fontSize: '13px',
  textTransform: 'none',
  border: `1px solid ${color === 'success' ? '#a7f3d0' : color === 'error' ? '#fecaca' : '#e2e8f0'}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: color === 'success' ? '#ecfdf5' : color === 'error' ? '#fef2f2' : '#f8fafc',
    border: `1px solid ${color === 'success' ? '#6ee7b7' : color === 'error' ? '#fca5a5' : '#cbd5e1'}`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    color: '#1e293b',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: '#e2e8f0',
      transition: 'all 0.2s ease',
    },
    '&:hover fieldset': {
      borderColor: '#cbd5e1',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#6366f1',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#64748b',
    fontSize: '14px',
    '&.Mui-focused': {
      color: '#6366f1',
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '10px',
  color: '#1e293b',
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e2e8f0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#cbd5e1',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6366f1',
    borderWidth: '2px',
  },
  '& .MuiSelect-icon': {
    color: '#64748b',
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const colors = {
    published: { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
    draft: { bg: '#fefce8', text: '#ca8a04', border: '#fde047' },
    archived: { bg: '#f1f5f9', text: '#64748b', border: '#cbd5e1' },
  };
  const color = colors[status] || colors.draft;
  return {
    backgroundColor: color.bg,
    color: color.text,
    border: `1px solid ${color.border}`,
    fontWeight: 600,
    fontSize: '11px',
    height: '24px',
    borderRadius: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };
});

const FeatureChip = styled(Chip)(({ theme, variant }) => {
  const colors = {
    test: { bg: '#fef3c7', text: '#b45309', border: '#fcd34d' },
    ai: { bg: '#eef2ff', text: '#4f46e5', border: '#c7d2fe' },
    required: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
  };
  const color = colors[variant] || colors.test;
  return {
    backgroundColor: color.bg,
    color: color.text,
    border: `1px solid ${color.border}`,
    fontWeight: 500,
    fontSize: '11px',
    height: '22px',
    borderRadius: '6px',
  };
});

const DocumentTypeChip = styled(Chip)(({ theme, docType }) => {
  const colors = {
    reference: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
    handbook: { bg: '#fce7f3', text: '#be185d', border: '#f9a8d4' },
    template: { bg: '#d1fae5', text: '#047857', border: '#6ee7b7' },
    policy: { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
    other: { bg: '#f3f4f6', text: '#4b5563', border: '#d1d5db' },
  };
  const color = colors[docType] || colors.other;
  return {
    backgroundColor: color.bg,
    color: color.text,
    border: `1px solid ${color.border}`,
    fontWeight: 500,
    fontSize: '10px',
    height: '20px',
    borderRadius: '4px',
    textTransform: 'capitalize',
  };
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    background: '#ffffff',
    borderRadius: '20px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
    color: '#1e293b',
    overflow: 'hidden',
  },
  '& .MuiDialogTitle-root': {
    padding: '24px 28px 16px',
    fontSize: '18px',
    fontWeight: 700,
    letterSpacing: '-0.3px',
    borderBottom: '1px solid #f1f5f9',
    background: '#fafafa',
  },
  '& .MuiDialogContent-root': {
    padding: '24px 28px',
  },
  '& .MuiDialogActions-root': {
    padding: '16px 28px 24px',
    borderTop: '1px solid #f1f5f9',
    background: '#fafafa',
  },
}));

const StatCard = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  borderRadius: '14px',
  padding: '20px 24px',
  border: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
    transform: 'translateY(-1px)',
  },
}));

const IconWrapper = styled(Box)(({ theme, color }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: color === 'primary' 
    ? 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)'
    : color === 'success'
    ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
    : color === 'warning'
    ? 'linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)'
    : color === 'training'
    ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
    : color === 'documents'
    ? 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)'
    : 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
  '& svg': {
    fontSize: '24px',
    color: color === 'primary' ? '#4f46e5'
      : color === 'success' ? '#059669'
      : color === 'warning' ? '#d97706'
      : color === 'training' ? '#15803d'
      : color === 'documents' ? '#a855f7'
      : '#4f46e5',
  },
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: '8px',
  borderRadius: '4px',
  backgroundColor: '#e2e8f0',
  '& .MuiLinearProgress-bar': {
    borderRadius: '4px',
    background: 'linear-gradient(90deg, #4f46e5 0%, #6366f1 100%)',
  },
}));

const DragHandle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px',
  borderRadius: '8px',
  color: '#94a3b8',
  cursor: 'grab',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: '#64748b',
    background: '#f1f5f9',
  },
  '&:active': {
    cursor: 'grabbing',
  },
}));

const FileUploadZone = styled(Box)(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? '#6366f1' : '#e2e8f0'}`,
  borderRadius: '12px',
  padding: '40px',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  background: isDragActive ? '#eef2ff' : '#fafafa',
  '&:hover': {
    border: '2px dashed #a5b4fc',
    background: '#f8fafc',
  },
}));

const ActionIconButton = styled(IconButton)(({ theme, variant }) => ({
  padding: '8px',
  borderRadius: '8px',
  color: variant === 'danger' ? '#dc2626' : '#64748b',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: variant === 'danger' ? '#fef2f2' : '#f1f5f9',
    color: variant === 'danger' ? '#b91c1c' : '#1e293b',
  },
}));

const ModuleNumber = styled(Box)(({ theme }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
  border: '1px solid #c7d2fe',
  color: '#4f46e5',
  fontWeight: 700,
  fontSize: '15px',
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: '#4f46e5',
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  color: '#64748b',
  minHeight: 48,
  '&.Mui-selected': {
    color: '#4f46e5',
  },
  '&:hover': {
    color: '#4f46e5',
    backgroundColor: '#f8fafc',
  },
}));

const SourceToggle = styled(ToggleButtonGroup)(({ theme }) => ({
  width: '100%',
  '& .MuiToggleButton-root': {
    flex: 1,
    padding: '12px 16px',
    border: '1px solid #e2e8f0',
    color: '#64748b',
    textTransform: 'none',
    fontWeight: 500,
    '&.Mui-selected': {
      background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
      color: '#4f46e5',
      borderColor: '#a5b4fc',
    },
    '&:hover': {
      background: '#f8fafc',
    },
  },
}));

const VideoPreviewBox = styled(Box)(({ theme }) => ({
  background: '#000',
  borderRadius: '12px',
  overflow: 'hidden',
  position: 'relative',
  paddingTop: '56.25%',
  '& iframe': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
  },
}));

const ProviderChip = styled(Chip)(({ theme, provider }) => {
  const colors = {
    youtube: { bg: '#fee2e2', text: '#dc2626', border: '#fca5a5' },
    vimeo: { bg: '#dbeafe', text: '#2563eb', border: '#93c5fd' },
    dailymotion: { bg: '#fef3c7', text: '#d97706', border: '#fcd34d' },
    wistia: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
    loom: { bg: '#f3e8ff', text: '#7c3aed', border: '#c4b5fd' },
    custom: { bg: '#f1f5f9', text: '#64748b', border: '#cbd5e1' },
  };
  const color = colors[provider] || colors.custom;
  return {
    backgroundColor: color.bg,
    color: color.text,
    border: `1px solid ${color.border}`,
    fontWeight: 600,
    fontSize: '11px',
    height: '24px',
    textTransform: 'capitalize',
  };
});

const ValidationWarningBox = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
  border: '1px solid #fecaca',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  marginBottom: '16px',
}));

// =============================================================================
// AI INSTRUCTOR STYLED COMPONENTS
// =============================================================================

const AIInstructorCard = styled(Box)(({ theme, isEnabled }) => ({
  background: isEnabled 
    ? 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)'
    : '#ffffff',
  borderRadius: '16px',
  border: isEnabled ? '1px solid #e879f9' : '1px solid #e2e8f0',
  padding: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(168, 85, 247, 0.15)',
  },
}));

const VoiceTypeButton = styled(Button)(({ theme, selected, voiceType }) => ({
  flex: 1,
  padding: '16px 24px',
  borderRadius: '12px',
  border: selected 
    ? `2px solid ${voiceType === 'FEMALE' ? '#ec4899' : '#3b82f6'}`
    : '1px solid #e2e8f0',
  background: selected 
    ? voiceType === 'FEMALE' 
      ? 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)'
      : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
    : '#ffffff',
  color: selected 
    ? voiceType === 'FEMALE' ? '#be185d' : '#1d4ed8'
    : '#64748b',
  textTransform: 'none',
  fontWeight: selected ? 600 : 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: voiceType === 'FEMALE' 
      ? 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)'
      : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    border: `2px solid ${voiceType === 'FEMALE' ? '#f472b6' : '#60a5fa'}`,
  },
}));

const ScriptTypeCard = styled(Box)(({ theme, isActive }) => ({
  background: isActive ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' : '#f8fafc',
  borderRadius: '12px',
  border: isActive ? '1px solid #6ee7b7' : '1px solid #e2e8f0',
  padding: '16px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: isActive ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' : '#f1f5f9',
    border: '1px solid #a7f3d0',
  },
}));

const AudioPreviewBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
  borderRadius: '16px',
  padding: '24px',
  color: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 30% 50%, rgba(139, 92, 246, 0.3) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
}));

const WaveformBar = styled(Box)(({ theme, height, isPlaying }) => ({
  width: '4px',
  height: `${height}px`,
  background: isPlaying 
    ? 'linear-gradient(180deg, #a78bfa 0%, #8b5cf6 100%)'
    : '#4c1d95',
  borderRadius: '2px',
  transition: 'height 0.1s ease',
  animation: isPlaying ? `${waveAnimation} 0.5s ease-in-out infinite` : 'none',
}));

const waveAnimation = keyframes`
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(1.5); }
`;

const ScriptPreviewBubble = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  borderRadius: '16px 16px 16px 4px',
  padding: '16px 20px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  maxWidth: '80%',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: -8,
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderWidth: '0 12px 12px 0',
    borderColor: 'transparent #ffffff transparent transparent',
  },
}));

const SourceTypeCard = styled(Box)(({ theme, selected }) => ({
  flex: 1,
  padding: '16px',
  borderRadius: '12px',
  border: selected ? '2px solid #6366f1' : '1px solid #e2e8f0',
  background: selected ? 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)' : '#ffffff',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  textAlign: 'center',
  '&:hover': {
    border: selected ? '2px solid #6366f1' : '1px solid #a5b4fc',
    background: selected ? 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)' : '#f8fafc',
  },
}));

const ExistingFileInfo = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
  border: '1px solid #bbf7d0',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
}));

// ============================================================================
// EDITOR & FILE GENERATION HELPERS
// ============================================================================

// Generate a unique filename with timestamp
const generateDynamicFilename = (title, contentType) => {
  const timestamp = Date.now();
  const sanitizedTitle = title.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
  const extension = contentType === 'slide' ? '.pdf' : '.docx';
  return `${sanitizedTitle}_${timestamp}${extension}`;
};

// ============================================================================
// FIXED: Convert HTML content to a DOCX file blob
// Fixes:
// 1. Named colors (red, blue, etc.) now converted to hex
// 2. Headings (H1, H2, H3, etc.) now properly styled with correct sizes
// ============================================================================

const convertHtmlToDocxBlob = async (htmlContent, title) => {
  const { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } = await import('docx');
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const elements = doc.body.children;
  
  const paragraphs = [];
  
  // Named color to hex mapping
  const namedColors = {
    black: '000000', white: 'FFFFFF', red: 'FF0000', green: '008000', blue: '0000FF',
    yellow: 'FFFF00', cyan: '00FFFF', magenta: 'FF00FF', gray: '808080', grey: '808080',
    orange: 'FFA500', pink: 'FFC0CB', purple: '800080', brown: 'A52A2A', navy: '000080',
    teal: '008080', olive: '808000', maroon: '800000', lime: '00FF00', aqua: '00FFFF',
    silver: 'C0C0C0', fuchsia: 'FF00FF', darkred: '8B0000', darkgreen: '006400',
    darkblue: '00008B', darkcyan: '008B8B', darkmagenta: '8B008B', darkyellow: '9B870C',
    lightgray: 'D3D3D3', lightgrey: 'D3D3D3', lightblue: 'ADD8E6', lightgreen: '90EE90',
    lightyellow: 'FFFFE0', lightpink: 'FFB6C1', coral: 'FF7F50', salmon: 'FA8072',
    khaki: 'F0E68C', gold: 'FFD700', indigo: '4B0082', violet: 'EE82EE', crimson: 'DC143C',
    turquoise: '40E0D0', chocolate: 'D2691E', tomato: 'FF6347', orangered: 'FF4500',
    steelblue: '4682B4', royalblue: '4169E1', slategray: '708090', slategrey: '708090',
  };

  // Helper to convert any color format to 6-digit hex (without #)
  const colorToHex = (color) => {
    if (!color) return null;
    color = color.trim().toLowerCase();
    
    // Already a hex color
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      if (hex.length === 3) {
        return hex.split('').map(c => c + c).join('');
      }
      return hex.length === 6 ? hex : null;
    }
    
    // RGB/RGBA format
    if (color.startsWith('rgb')) {
      const rgb = color.match(/\d+/g);
      if (rgb && rgb.length >= 3) {
        return rgb.slice(0, 3).map(x => parseInt(x).toString(16).padStart(2, '0')).join('');
      }
      return null;
    }
    
    // Named color
    if (namedColors[color]) {
      return namedColors[color];
    }
    
    return null;
  };

  // Helper to extract color from style
  const extractColor = (element) => {
    const style = element.getAttribute?.('style') || '';
    const colorMatch = style.match(/(?:^|;)\s*color:\s*([^;]+)/i);
    if (colorMatch) {
      return colorToHex(colorMatch[1]);
    }
    return null;
  };

  // Helper to extract background/highlight color
  const extractHighlight = (element) => {
    const style = element.getAttribute?.('style') || '';
    const bgMatch = style.match(/background-color:\s*([^;]+)/i);
    if (bgMatch) {
      return colorToHex(bgMatch[1]);
    }
    return null;
  };

  // Helper to get alignment
  const getAlignment = (element) => {
    const style = element.getAttribute?.('style') || '';
    const alignMatch = style.match(/text-align:\s*([^;]+)/i);
    if (alignMatch) {
      const align = alignMatch[1].trim().toLowerCase();
      switch (align) {
        case 'center': return AlignmentType.CENTER;
        case 'right': return AlignmentType.RIGHT;
        case 'justify': return AlignmentType.JUSTIFIED;
        default: return AlignmentType.LEFT;
      }
    }
    const className = element.className || '';
    if (className.includes('text-center')) return AlignmentType.CENTER;
    if (className.includes('text-right')) return AlignmentType.RIGHT;
    return AlignmentType.LEFT;
  };

  // Heading size mapping (in half-points, so 32 = 16pt)
  const headingSizes = {
    h1: 48,  // 24pt
    h2: 40,  // 20pt  
    h3: 32,  // 16pt
    h4: 28,  // 14pt
    h5: 24,  // 12pt
    h6: 22,  // 11pt
  };

  // Helper to process inline elements and extract text runs with formatting
  const processInlineContent = (element, defaultOptions = {}) => {
    const textRuns = [];
    
    const processNode = (node, inheritedStyles = {}) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text && text.length > 0) {
          const runOptions = {
            text: text,
            size: inheritedStyles.size || defaultOptions.size || 22,
            bold: inheritedStyles.bold || defaultOptions.bold || false,
            italics: inheritedStyles.italics || defaultOptions.italics || false,
          };
          
          if (inheritedStyles.underline || defaultOptions.underline) {
            runOptions.underline = {};
          }
          
          if (inheritedStyles.color) {
            runOptions.color = inheritedStyles.color;
          }
          if (inheritedStyles.highlight) {
            runOptions.highlight = inheritedStyles.highlight;
          }
          
          textRuns.push(new TextRun(runOptions));
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        const newStyles = { ...inheritedStyles };
        
        // Check for inline styles
        const color = extractColor(node);
        if (color) newStyles.color = color;
        
        const highlight = extractHighlight(node);
        if (highlight) newStyles.highlight = highlight;
        
        // Check for formatting tags
        if (tagName === 'strong' || tagName === 'b') newStyles.bold = true;
        if (tagName === 'em' || tagName === 'i') newStyles.italics = true;
        if (tagName === 'u') newStyles.underline = true;
        if (tagName === 'mark') {
          newStyles.highlight = extractHighlight(node) || 'FFFF00';
        }
        
        // Process children
        node.childNodes.forEach(child => processNode(child, newStyles));
      }
    };
    
    processNode(element);
    
    // If no text runs were created, create a default one
    if (textRuns.length === 0) {
      const text = element.textContent || '';
      if (text.trim()) {
        textRuns.push(new TextRun({ 
          text: text, 
          size: defaultOptions.size || 22,
          bold: defaultOptions.bold || false,
          italics: defaultOptions.italics || false,
        }));
      }
    }
    
    return textRuns;
  };

  // Add title
  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: title,
          bold: true,
          size: 48, // 24pt
        }),
      ],
      spacing: { after: 400 },
      alignment: AlignmentType.LEFT,
    })
  );
  
  // Process HTML elements
  const processElement = (element) => {
    const tagName = element.tagName?.toLowerCase();
    const alignment = getAlignment(element);
    
    // Handle headings with proper sizing
    if (tagName && tagName.match(/^h[1-6]$/)) {
      const headingSize = headingSizes[tagName] || 22;
      const textRuns = processInlineContent(element, { size: headingSize, bold: true });
      
      return new Paragraph({
        children: textRuns,
        spacing: { before: 300, after: 200 },
        alignment: alignment,
      });
    }
    
    // Handle paragraphs
    if (tagName === 'p') {
      const textRuns = processInlineContent(element, { size: 22 });
      return new Paragraph({
        children: textRuns,
        spacing: { after: 200 },
        alignment: alignment,
      });
    }
    
    // Handle list items
    if (tagName === 'li') {
      const textRuns = processInlineContent(element, { size: 22 });
      return new Paragraph({
        children: [new TextRun({ text: '• ', size: 22 }), ...textRuns],
        spacing: { after: 100 },
        alignment: alignment,
      });
    }
    
    // Handle blockquotes
    if (tagName === 'blockquote') {
      const textRuns = processInlineContent(element, { size: 22, italics: true });
      return new Paragraph({
        children: textRuns,
        spacing: { after: 200 },
        indent: { left: 720 },
        alignment: alignment,
      });
    }
    
    // Handle code blocks
    if (tagName === 'pre' || tagName === 'code') {
      const text = element.textContent || '';
      return new Paragraph({
        children: [
          new TextRun({
            text: text,
            size: 20,
            font: 'Courier New',
          }),
        ],
        spacing: { after: 200 },
      });
    }
    
    // Handle divs and other containers
    if (tagName === 'div' || tagName === 'span') {
      const textRuns = processInlineContent(element, { size: 22 });
      if (textRuns.length > 0) {
        return new Paragraph({
          children: textRuns,
          spacing: { after: 200 },
          alignment: alignment,
        });
      }
      return null;
    }
    
    // Default handling for any other element with text
    if (element.textContent?.trim()) {
      const textRuns = processInlineContent(element, { size: 22 });
      return new Paragraph({
        children: textRuns,
        spacing: { after: 200 },
        alignment: alignment,
      });
    }
    
    return null;
  };
  
  // Process all elements
  Array.from(elements).forEach((element) => {
    const tagName = element.tagName?.toLowerCase();
    
    if (tagName === 'ul' || tagName === 'ol') {
      // Process list items
      Array.from(element.children).forEach((li) => {
        const para = processElement(li);
        if (para) paragraphs.push(para);
      });
    } else {
      const para = processElement(element);
      if (para) paragraphs.push(para);
    }
  });
  
  // Create the document
  const document = new Document({
    sections: [
      {
        properties: {},
        children: paragraphs.length > 1 ? paragraphs : [
          new Paragraph({
            children: [new TextRun({ text: htmlContent.replace(/<[^>]*>/g, ''), size: 22 })],
          }),
        ],
      },
    ],
  });
  
  // Generate blob
  const blob = await Packer.toBlob(document);
  return blob;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const CourseBuilder = () => {
  const { chatbotId, courseId } = useParams();
  const navigate = useNavigate();

  // State
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishingModuleId, setPublishingModuleId] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  // Training State
  const [trainingContent, setTrainingContent] = useState({});
  const [bulkTrainingSubmodule, setBulkTrainingSubmodule] = useState(null);
  const [trainingMenuAnchor, setTrainingMenuAnchor] = useState(null);
  const [selectedContentForMenu, setSelectedContentForMenu] = useState(null);
  const [trainingDataDialog, setTrainingDataDialog] = useState({ open: false, content: null });
  const [trainingDataForm, setTrainingDataForm] = useState({ page_content: '', summary: '', tags: '' });

  // Supporting Documents State
  const [supportingDocuments, setSupportingDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [supportingDocDialog, setSupportingDocDialog] = useState({ open: false, document: null, editMode: false });
  const [supportingDocForm, setSupportingDocForm] = useState({
    title: '', description: '', document_type: 'reference',
    is_downloadable: true, is_visible: true, is_training_enabled: false, access_type: 'enrolled'
  });
  const [supportingDocFile, setSupportingDocFile] = useState(null);
  const [trainingDocuments, setTrainingDocuments] = useState({});
  const [bulkTrainingDocuments, setBulkTrainingDocuments] = useState(false);
  const [documentMenuAnchor, setDocumentMenuAnchor] = useState(null);
  const [selectedDocumentForMenu, setSelectedDocumentForMenu] = useState(null);
  const [documentTrainingDataDialog, setDocumentTrainingDataDialog] = useState({ open: false, document: null });
  const [documentTrainingDataForm, setDocumentTrainingDataForm] = useState({ page_content: '', summary: '', tags: '' });

  // Dialogs
  const [moduleDialog, setModuleDialog] = useState({ open: false, module: null, editMode: false });
  const [submoduleDialog, setSubmoduleDialog] = useState({ open: false, moduleId: null, submodule: null });
  const [contentDialog, setContentDialog] = useState({ open: false, submoduleId: null, content: null });
  const [testDialog, setTestDialog] = useState({ open: false, moduleId: null });
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [transcriptDialog, setTranscriptDialog] = useState({ open: false, contentId: null });

  // Update the contentForm state to include new fields
  const [contentForm, setContentForm] = useState({
    title: '',
    description: '',
    content_type: 'document',
    is_required: true,
    video_duration: 0,
    source_type: 'upload', // 'upload', 'url', 'create'
    external_url: '',
    editor_content: '', // For storing editor HTML content
  });

  const [validationDialog, setValidationDialog] = useState({ 
    open: false, 
    errors: [], 
    type: '',
    onConfirm: null 
  });


  // Add state for tracking if we're editing and the existing content
  const [editingContent, setEditingContent] = useState(null);

  
  // Add TipTap Editor Hook - place this after other useState declarations
  const contentEditor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Underline,
      TiptapTable.configure({ resizable: true }),  // Use renamed import
      TiptapTableCell,
      TiptapTableHeader,
      TiptapTableRow,
      TiptapLink.configure({  // Use renamed import
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: '',
    editorProps: {
      attributes: { class: "tiptap-editor-area p-3 form-control" },
    },
  });

  // Forms
  const [moduleForm, setModuleForm] = useState({
    title: '', description: '', sort_order: 0, requires_knowledge_test: true, min_passing_score: 70
  });
  const [submoduleForm, setSubmoduleForm] = useState({ title: '', description: '', is_training_enabled: false });
  const [videoPreview, setVideoPreview] = useState(null);
  const [courseSettings, setCourseSettings] = useState({
    title: '', description: '', short_description: '', category: '',
    difficulty_level: 'beginner', estimated_duration: 0, passing_score: 70, is_certificate_enabled: true,
    thumbnail_url: ''
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  const [uploadFile, setUploadFile] = useState(null);
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  
// Reset content form with editor
const resetContentFormWithEditor = () => {
  setContentForm({
    title: '',
    description: '',
    content_type: 'document',
    is_required: true,
    video_duration: 0,
    source_type: 'upload',
    external_url: '',
    editor_content: '',
  });
  setUploadFile(null);
  setVideoPreview(null);
  setEditingContent(null);
  if (contentEditor) {
    contentEditor.commands.clearContent();
  }
};

// Load content for editing
const loadContentForEditing = async (content) => {
  setEditingContent(content);
  setContentForm({
    title: content.title || '',
    description: content.description || '',
    content_type: content.content_type || 'document',
    is_required: content.is_required ?? true,
    video_duration: content.video_duration || 0,
    source_type: content.file_url ? (content.embed_code ? 'url' : 'upload') : 'create',
    external_url: content.file_url || '',
    editor_content: '',
  });
  
  // If there's existing content and we want to edit it in the editor
  if (content.page_content && contentEditor) {
    contentEditor.commands.setContent(content.page_content);
  }
  
  setUploadFile(null);
  if (content.content_type === 'video' && content.file_url) {
    const parsed = parseVideoUrl(content.file_url);
    setVideoPreview(parsed);
  } else {
    setVideoPreview(null);
  }
};

  // =============================================================================
  // AI INSTRUCTOR STATE
  // =============================================================================
  const [aiInstructorSettings, setAiInstructorSettings] = useState({
    enabled: true,
    instructor_name: 'AI Instructor',
    voice_type: 'FEMALE',
    language: 'en',
    avatar: {
      image_png: null,
      image_gif: null,
      gender: 'female'
    },
    features: {
      course_welcome: true,
      module_intro: true,
      module_complete: true,
      test_intro: true,
      test_feedback: true,
      course_complete: true,
      encouragement: true
    },
    custom_scripts: {}
  });
  const [loadingAISettings, setLoadingAISettings] = useState(false);
  const [aiInstructorDialog, setAiInstructorDialog] = useState(false);
  const [scriptPreviewDialog, setScriptPreviewDialog] = useState({ open: false, scriptType: null });
  const [previewingScript, setPreviewingScript] = useState(false);
  const [previewScriptData, setPreviewScriptData] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(false);
  const [audioElement, setAudioElement] = useState(null);
  const [savingAISettings, setSavingAISettings] = useState(false);
  const [customScriptDialog, setCustomScriptDialog] = useState({ open: false, scriptType: null });
  const [customScriptForm, setCustomScriptForm] = useState({ script_text: '', generate_audio: true });

  // Helper Functions

  // Validate course before publishing
  const validateCourseForPublishing = () => {
    const errors = [];
    
    // Check if course has any modules
    if (!modules || modules.length === 0) {
      errors.push({
        type: 'error',
        message: 'Course must have at least one module before publishing.',
        suggestion: 'Add at least one module to your course.'
      });
      return errors;
    }
    
    // Check if there are any published modules
    const publishedModules = modules.filter(m => m.status === 'published');
    if (publishedModules.length === 0) {
      errors.push({
        type: 'error',
        message: 'Course must have at least one published module before publishing.',
        suggestion: 'Publish at least one module first.'
      });
    }
    
    // Check each module
    modules.forEach((module, moduleIndex) => {
      const moduleNumber = moduleIndex + 1;
      
      // Check if module has submodules
      if (!module.submodules || module.submodules.length === 0) {
        errors.push({
          type: 'warning',
          message: `Module ${moduleNumber} "${module.title}" has no submodules.`,
          suggestion: 'Add at least one submodule with content to this module.'
        });
      } else {
        // Check if any submodule has content
        const hasContentInAnySubmodule = module.submodules.some(
          sub => sub.content_items && sub.content_items.length > 0
        );
        
        if (!hasContentInAnySubmodule) {
          errors.push({
            type: 'warning',
            message: `Module ${moduleNumber} "${module.title}" has submodules but no content.`,
            suggestion: 'Add content to at least one submodule in this module.'
          });
        }
        
        // Check each submodule for empty content
        module.submodules.forEach((submodule) => {
          if (!submodule.content_items || submodule.content_items.length === 0) {
            errors.push({
              type: 'info',
              message: `Submodule "${submodule.title}" in Module ${moduleNumber} has no content.`,
              suggestion: 'Consider adding content to this submodule or removing it.'
            });
          }
        });
      }
      
      // Check if module requires test but doesn't have one
      if (module.requires_knowledge_test && !module.knowledge_test) {
        errors.push({
          type: 'warning',
          message: `Module ${moduleNumber} "${module.title}" requires a knowledge test but none is configured.`,
          suggestion: 'Create a knowledge test for this module or disable the test requirement.'
        });
      }
    });
    
    return errors;
  };

  // Validate module before publishing
  const validateModuleForPublishing = (module) => {
    const errors = [];
    
    // Check if module has submodules
    if (!module.submodules || module.submodules.length === 0) {
      errors.push({
        type: 'error',
        message: 'Module must have at least one submodule before publishing.',
        suggestion: 'Add at least one submodule to this module.'
      });
      return errors;
    }
    
    // Check if any submodule has content
    const hasContentInAnySubmodule = module.submodules.some(
      sub => sub.content_items && sub.content_items.length > 0
    );
    
    if (!hasContentInAnySubmodule) {
      errors.push({
        type: 'error',
        message: 'Module must have at least one submodule with content before publishing.',
        suggestion: 'Add content to at least one submodule.'
      });
    }
    
    // Check empty submodules
    module.submodules.forEach((submodule) => {
      if (!submodule.content_items || submodule.content_items.length === 0) {
        errors.push({
          type: 'warning',
          message: `Submodule "${submodule.title}" has no content.`,
          suggestion: 'Add content to this submodule or consider removing it.'
        });
      }
    });
    
    // Check if module requires test but doesn't have one
    if (module.requires_knowledge_test && !module.knowledge_test) {
      errors.push({
        type: 'warning',
        message: 'This module requires a knowledge test but none is configured.',
        suggestion: 'Create a knowledge test or disable the test requirement.'
      });
    }
    
    return errors;
  };

  // Validate file type matches content type
  const validateFileTypeMatch = (file, contentType) => {
    if (!file) return { valid: true };
    
    const detectedType = getContentTypeFromExtension(file.name);
    
    if (!detectedType) {
      return {
        valid: false,
        message: `Unsupported file type. Please upload a valid ${getContentTypeLabel(contentType)} file.`,
        detectedType: null,
        expectedType: contentType
      };
    }
    
    if (detectedType !== contentType) {
      return {
        valid: false,
        message: `File type mismatch: You selected "${getContentTypeLabel(contentType)}" but uploaded a ${getContentTypeLabel(detectedType)} file (${file.name}).`,
        detectedType: detectedType,
        expectedType: contentType,
        suggestion: `Either change the content type to "${getContentTypeLabel(detectedType)}" or upload a different file.`
      };
    }
    
    return { valid: true, detectedType };
  };

  const countTrainedContent = () => {
    let total = 0, trained = 0;
    modules.forEach(m => {
      m.submodules?.forEach(s => {
        if (s.is_training_enabled) {
          s.content_items?.forEach(c => { total++; if (c.is_trained) trained++; });
        }
      });
    });
    return { total, trained };
  };

  const countTrainedDocuments = () => {
    const total = supportingDocuments.length;
    const trained = supportingDocuments.filter(d => d.is_trained).length;
    return { total, trained };
  };

  const getTrainingStatus = (content) => {
    if (content.training_error) return 'error';
    if (content.is_trained) return 'trained';
    if (content.is_scraped) return 'scraped';
    return 'pending';
  };

  const getTrainingStatusLabel = (content) => {
    if (content.training_error) return 'Error';
    if (content.is_trained) return 'Trained';
    if (content.is_scraped) return 'Scraped';
    return 'Not Trained';
  };

  const canTrain = (content, submodule) => {
    if (!submodule?.is_training_enabled) return false;
    if (content.content_type === 'video') {
      return content.transcript_text || content.transcript_file_url || content.has_transcript;
    }
    return content.file_url;
  };

  const canTrainDocument = (doc) => doc.file_url && doc.is_training_enabled;

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const getContentIcon = (type) => {
    const icons = {
      video: <VideoIcon sx={{ color: '#dc2626' }} />,
      slide: <PdfIcon sx={{ color: '#d97706' }} />,
      document: <DocumentIcon sx={{ color: '#2563eb' }} />,
    };
    return icons[type] || icons.document;
  };

  const getDocumentIcon = (docType) => {
    const icons = {
      reference: <LibraryBooksIcon sx={{ color: '#1d4ed8' }} />,
      handbook: <DescriptionIcon sx={{ color: '#be185d' }} />,
      template: <FileCopyIcon sx={{ color: '#047857' }} />,
      policy: <PolicyIcon sx={{ color: '#b91c1c' }} />,
      other: <AttachFileIcon sx={{ color: '#4b5563' }} />,
    };
    return icons[docType] || icons.other;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const calculateProgress = () => {
    if (!modules.length) return 0;
    const published = modules.filter(m => m.status === 'published').length;
    return Math.round((published / modules.length) * 100);
  };

  const toggleModuleExpand = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  // Reset content form helper
  const resetContentForm = () => {
    setContentForm({
      title: '', description: '', content_type: 'document', is_required: true, 
      video_duration: 0, source_type: 'upload', external_url: ''
    });
    setUploadFile(null);
    setVideoPreview(null);
  };

  // Update your existing useEffect
  useEffect(() => {
    loadCourse();
    loadModules();
    loadSupportingDocuments();
    loadAIInstructorSettings(); // Add this line
  }, [courseId]);

  // Data Loading
  const loadCourse = async () => {
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await axiosInstance.get(`/api/lms/courses/${courseId}`, { params });
      setCourse(response.data.data);
      setCourseSettings({
        title: response.data.data.title,
        description: response.data.data.description || '',
        short_description: response.data.data.short_description || '',
        category: response.data.data.category || '',
        difficulty_level: response.data.data.difficulty_level || 'beginner',
        estimated_duration: response.data.data.estimated_duration || 0,
        passing_score: response.data.data.passing_score || 70,
        is_certificate_enabled: response.data.data.is_certificate_enabled || true,
        thumbnail_url: response.data.data.thumbnail_url || ''
      });
      setThumbnailPreview(null);
      setThumbnailFile(null);
    } catch (error) {
      showSnackbar('Error loading course', 'error');
    }
  };

  const loadModules = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/lms/courses/${courseId}/modules`, {
        params: { include_submodules: true, include_tests: true }
      });
      setModules(response.data.data);
      if (response.data.data.length > 0) {
        setExpandedModules({ [response.data.data[0].id]: true });
      }
    } catch (error) {
      showSnackbar('Error loading modules', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadSupportingDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const response = await axiosInstance.get(`/api/lms/courses/${courseId}/supporting-documents`);
      setSupportingDocuments(response.data.data || []);
    } catch (error) {
      console.error('Error loading supporting documents:', error);
      showSnackbar('Error loading supporting documents', 'error');
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleFileUpload = (file) => {
  if (!file) {
    setUploadFile(null);
    return;
  }
  
  const detectedType = getContentTypeFromExtension(file.name);
  
  if (detectedType) {
    // Auto-update content type based on file extension
    setContentForm(prev => ({
      ...prev,
      content_type: detectedType
    }));
    setUploadFile(file);
    
    // Show info message about auto-detection
    showSnackbar(`Content type automatically set to "${getContentTypeLabel(detectedType)}" based on file type.`, 'info');
  } else {
    // Unknown file type - warn user
    showSnackbar(`Unknown file type. Please select the appropriate content type manually.`, 'warning');
    setUploadFile(file);
  }
};


  // Handle video URL change
  const handleVideoUrlChange = (url) => {
    setContentForm(prev => ({ ...prev, external_url: url }));
    if (!url) {
      setVideoPreview(null);
      return;
    }
    const parsed = parseVideoUrl(url);
    setVideoPreview(parsed);
  };

  // Handle content type change - FIXED
  const handleContentTypeChange = (newType) => {
    setContentForm(prev => ({ 
      ...prev, 
      content_type: newType,
      source_type: 'upload',
      external_url: ''
    }));
    setUploadFile(null);
    setVideoPreview(null);
  };

  // Supporting Documents Operations
  const handleUploadSupportingDocument = async () => {
    if (!supportingDocForm.title.trim()) {
      showSnackbar('Document title is required', 'error');
      return;
    }
    if (!supportingDocFile && !supportingDocDialog.editMode) {
      showSnackbar('Please select a file to upload', 'error');
      return;
    }
    setSaving(true);
    try {
      if (supportingDocDialog.editMode && supportingDocDialog.document) {
        await axiosInstance.put(`/api/lms/supporting-documents/${supportingDocDialog.document.id}`, {
          title: supportingDocForm.title, description: supportingDocForm.description,
          document_type: supportingDocForm.document_type, is_downloadable: supportingDocForm.is_downloadable,
          is_visible: supportingDocForm.is_visible, is_training_enabled: supportingDocForm.is_training_enabled,
          access_type: supportingDocForm.access_type
        });
        showSnackbar('Document updated successfully', 'success');
      } else {
        const formData = new FormData();
        formData.append('file', supportingDocFile);
        formData.append('title', supportingDocForm.title);
        formData.append('description', supportingDocForm.description || '');
        formData.append('document_type', supportingDocForm.document_type);
        formData.append('is_downloadable', supportingDocForm.is_downloadable);
        formData.append('is_visible', supportingDocForm.is_visible);
        formData.append('is_training_enabled', supportingDocForm.is_training_enabled);
        formData.append('access_type', supportingDocForm.access_type);
        await axiosInstance.post(`/api/lms/courses/${courseId}/supporting-documents/upload`, formData,
          { headers: { 'Content-Type': 'multipart/form-data' } });
        showSnackbar('Document uploaded successfully', 'success');
      }
      setSupportingDocDialog({ open: false, document: null, editMode: false });
      setSupportingDocForm({
        title: '', description: '', document_type: 'reference',
        is_downloadable: true, is_visible: true, is_training_enabled: false, access_type: 'enrolled'
      });
      setSupportingDocFile(null);
      loadSupportingDocuments();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving document', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSupportingDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axiosInstance.delete(`/api/lms/supporting-documents/${documentId}`);
      showSnackbar('Document deleted successfully', 'success');
      loadSupportingDocuments();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting document', 'error');
    }
  };

  // Supporting Document Training Operations
  const handleToggleDocumentTraining = async (documentId, enable) => {
    setTrainingDocuments(prev => ({ ...prev, [documentId]: 'loading' }));
    try {
      const response = await axiosInstance.post(`/api/lms/supporting-documents/${documentId}/toggle-training`, { enabled: enable });
      if (response.data.success) {
        showSnackbar(enable ? 'Document trained successfully' : 'Document removed from training', 'success');
        loadSupportingDocuments();
      } else {
        throw new Error(response.data.error || 'Training operation failed');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.error || error.message || 'Training operation failed', 'error');
    } finally {
      setTrainingDocuments(prev => ({ ...prev, [documentId]: null }));
    }
  };

  const handleScrapeDocument = async (documentId) => {
    setTrainingDocuments(prev => ({ ...prev, [documentId]: 'scraping' }));
    try {
      const response = await axiosInstance.post(`/api/lms/supporting-documents/${documentId}/scrape`);
      if (response.data.success) {
        showSnackbar('Document content extracted successfully', 'success');
        loadSupportingDocuments();
      } else {
        throw new Error(response.data.error || 'Extraction failed');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Extraction failed', 'error');
    } finally {
      setTrainingDocuments(prev => ({ ...prev, [documentId]: null }));
    }
  };

  const handleTrainDocument = async (documentId) => {
    setTrainingDocuments(prev => ({ ...prev, [documentId]: 'training' }));
    try {
      const response = await axiosInstance.post(`/api/lms/supporting-documents/${documentId}/train`);
      if (response.data.success) {
        showSnackbar('Document trained successfully', 'success');
        loadSupportingDocuments();
      } else {
        throw new Error(response.data.error || 'Training failed');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Training failed', 'error');
    } finally {
      setTrainingDocuments(prev => ({ ...prev, [documentId]: null }));
    }
  };

  const handleUntrainDocument = async (documentId) => {
    setTrainingDocuments(prev => ({ ...prev, [documentId]: 'untraining' }));
    try {
      const response = await axiosInstance.post(`/api/lms/supporting-documents/${documentId}/untrain`);
      if (response.data.success) {
        showSnackbar('Document removed from training', 'success');
        loadSupportingDocuments();
      } else {
        throw new Error(response.data.error || 'Untrain failed');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Untrain failed', 'error');
    } finally {
      setTrainingDocuments(prev => ({ ...prev, [documentId]: null }));
    }
  };

  const handleBulkTrainDocuments = async () => {
    setBulkTrainingDocuments(true);
    try {
      const response = await axiosInstance.post(`/api/lms/courses/${courseId}/supporting-documents/train-all`);
      if (response.data.success) {
        const { success_count, error_count, skipped_count } = response.data.data;
        showSnackbar(`Bulk training: ${success_count} succeeded, ${error_count} failed, ${skipped_count} skipped`,
          error_count > 0 ? 'warning' : 'success');
        loadSupportingDocuments();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Bulk training failed', 'error');
    } finally {
      setBulkTrainingDocuments(false);
    }
  };

  const openDocumentTrainingDataDialog = async (doc) => {
    try {
      const response = await axiosInstance.get(`/api/lms/supporting-documents/${doc.id}`,
        { params: { include_training_data: true } });
      const docData = response.data.data;
      setDocumentTrainingDataForm({
        page_content: docData.training_data?.page_content || '',
        summary: docData.training_data?.summary || '',
        tags: docData.training_data?.tags || ''
      });
      setDocumentTrainingDataDialog({ open: true, document: docData });
      setDocumentMenuAnchor(null);
    } catch (error) {
      showSnackbar('Error loading training data', 'error');
    }
  };

  const handleUpdateDocumentTrainingData = async () => {
    if (!documentTrainingDataDialog.document) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/api/lms/supporting-documents/${documentTrainingDataDialog.document.id}`, {
        page_content: documentTrainingDataForm.page_content,
        summary: documentTrainingDataForm.summary,
        tags: documentTrainingDataForm.tags
      });
      showSnackbar('Training data updated successfully', 'success');
      setDocumentTrainingDataDialog({ open: false, document: null });
      loadSupportingDocuments();
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Module Operations
  const handleCreateModule = async () => {
    if (!moduleForm.title.trim()) { showSnackbar('Module title is required', 'error'); return; }
    try {
      await axiosInstance.post(`/api/lms/courses/${courseId}/modules`, moduleForm);
      showSnackbar('Module created successfully', 'success');
      setModuleDialog({ open: false, module: null, editMode: false });
      setModuleForm({ title: '', description: '', sort_order: 0, requires_knowledge_test: true, min_passing_score: 70 });
      loadModules();
    } catch (error) { showSnackbar('Error creating module', 'error'); }
  };

  const handleUpdateModule = async () => {
    if (!moduleDialog.module) return;
    try {
      await axiosInstance.put(`/api/lms/modules/${moduleDialog.module.id}`, moduleForm);
      showSnackbar('Module updated successfully', 'success');
      setModuleDialog({ open: false, module: null, editMode: false });
      loadModules();
    } catch (error) { showSnackbar('Error updating module', 'error'); }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Are you sure you want to delete this module?')) return;
    try {
      await axiosInstance.delete(`/api/lms/modules/${moduleId}`);
      showSnackbar('Module deleted successfully', 'success');
      loadModules();
    } catch (error) { showSnackbar(error.response?.data?.message || 'Error deleting module', 'error'); }
  };

  // Execute module publish after validation
  const executePublishModule = async (moduleId) => {
    setPublishingModuleId(moduleId);
    try {
      await axiosInstance.post(`/api/lms/modules/${moduleId}/publish`, { publish: true });
      showSnackbar('Module published successfully', 'success');
      setModules(prev => prev.map(m => m.id === moduleId ? { ...m, status: 'published' } : m));
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to publish module', 'error');
    } finally { 
      setPublishingModuleId(null); 
    }
  };


  const handlePublishModule = async (moduleId) => {
    // Find the module to validate
    const moduleToPublish = modules.find(m => m.id === moduleId);
    if (!moduleToPublish) {
      showSnackbar('Module not found', 'error');
      return;
    }
    
    // Validate module
    const validationErrors = validateModuleForPublishing(moduleToPublish);
    const hasBlockingErrors = validationErrors.some(e => e.type === 'error');
    
    if (hasBlockingErrors) {
      setValidationDialog({
        open: true,
        errors: validationErrors,
        type: 'module',
        onConfirm: null // No confirm action for blocking errors
      });
      return;
    }
    
    // If there are warnings, show them but allow proceeding
    if (validationErrors.length > 0) {
      setValidationDialog({
        open: true,
        errors: validationErrors,
        type: 'module',
        onConfirm: async () => {
          await executePublishModule(moduleId);
          setValidationDialog({ open: false, errors: [], type: '', onConfirm: null });
        }
      });
      return;
    }
    
    // No errors, proceed directly
    await executePublishModule(moduleId);
  };

  // Submodule Operations
  const handleCreateSubmodule = async () => {
    if (!submoduleForm.title.trim()) { showSnackbar('Submodule title is required', 'error'); return; }
    try {
      await axiosInstance.post(`/api/lms/modules/${submoduleDialog.moduleId}/submodules`, submoduleForm);
      showSnackbar('Submodule created successfully', 'success');
      setSubmoduleDialog({ open: false, moduleId: null, submodule: null });
      setSubmoduleForm({ title: '', description: '', is_training_enabled: false });
      loadModules();
    } catch (error) { showSnackbar('Error creating submodule', 'error'); }
  };

  const handleUpdateSubmodule = async () => {
    if (!submoduleDialog.submodule) return;
    try {
      await axiosInstance.put(`/api/lms/submodules/${submoduleDialog.submodule.id}`, submoduleForm);
      showSnackbar('Submodule updated successfully', 'success');
      setSubmoduleDialog({ open: false, moduleId: null, submodule: null });
      loadModules();
    } catch (error) { showSnackbar('Error updating submodule', 'error'); }
  };

  const handleDeleteSubmodule = async (submoduleId) => {
    if (!window.confirm('Are you sure you want to delete this submodule?')) return;
    try {
      await axiosInstance.delete(`/api/lms/submodules/${submoduleId}`);
      showSnackbar('Submodule deleted successfully', 'success');
      loadModules();
    } catch (error) { showSnackbar('Error deleting submodule', 'error'); }
  };

  // =============================================================================
  // AI INSTRUCTOR FUNCTIONS
  // =============================================================================

  const loadAIInstructorSettings = async () => {
    setLoadingAISettings(true);
    try {
      const response = await axiosInstance.get(`/api/lms/courses/${courseId}/ai-instructor/settings`);
      if (response.data.success) {
        setAiInstructorSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error loading AI instructor settings:', error);
      // Use default settings on error
    } finally {
      setLoadingAISettings(false);
    }
  };

  const handleSaveAIInstructorSettings = async () => {
    setSavingAISettings(true);
    try {
      const response = await axiosInstance.put(`/api/lms/courses/${courseId}/ai-instructor/settings`, aiInstructorSettings);
      if (response.data.success) {
        showSnackbar('AI Instructor settings saved successfully', 'success');
        setAiInstructorDialog(false);
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving AI Instructor settings', 'error');
    } finally {
      setSavingAISettings(false);
    }
  };

  const handlePreviewScript = async (scriptType) => {
    setPreviewingScript(true);
    setScriptPreviewDialog({ open: true, scriptType });
    try {
      const response = await axiosInstance.post(`/api/lms/courses/${courseId}/ai-instructor/generate-script`, {
        script_type: scriptType,
        generate_audio: true,
        voice_type: aiInstructorSettings.voice_type,
        lang_type: aiInstructorSettings.language,
        context: {
          student_name: 'Student',
          course_title: course?.title || 'Course',
          module_title: 'Module 1',
          score: 85
        }
      });
      if (response.data.success) {
        setPreviewScriptData(response.data.data);
      }
    } catch (error) {
      showSnackbar('Error generating script preview', 'error');
      setPreviewScriptData({
        script_text: getDefaultScriptText(scriptType),
        audio_base64: null,
        is_fallback: true
      });
    } finally {
      setPreviewingScript(false);
    }
  };

  const getDefaultScriptText = (scriptType) => {
    const defaults = {
      course_welcome: "Welcome! I'm your AI instructor. I'm excited to guide you through this learning journey. Let's get started!",
      module_intro: "Great progress! You're about to start a new module. Pay attention to the key concepts, and you'll do great!",
      module_complete: "Excellent work! You've completed this module. Your dedication is impressive!",
      test_intro: "It's time to test your knowledge. Take your time, read each question carefully, and trust your preparation. Good luck!",
      test_pass: "Outstanding! You passed the test! Your hard work has paid off. Ready for the next challenge?",
      test_fail: "Don't worry. Learning takes time. Review the material and try again when you're ready. I believe in you!",
      course_complete: "Congratulations! You've completed the course! This is a wonderful achievement. Well done!",
      encouragement: "You're doing great! Keep up the excellent work!"
    };
    return defaults[scriptType] || defaults.encouragement;
  };

  const handlePlayAudio = () => {
    if (!previewScriptData?.audio_base64) return;
    
    if (playingAudio && audioElement) {
      audioElement.pause();
      setPlayingAudio(false);
      return;
    }

    const audio = new Audio(`data:audio/mp3;base64,${previewScriptData.audio_base64}`);
    audio.onended = () => setPlayingAudio(false);
    audio.play();
    setAudioElement(audio);
    setPlayingAudio(true);
  };

  const handleSaveCustomScript = async () => {
    if (!customScriptDialog.scriptType) return;
    
    setSavingAISettings(true);
    try {
      const response = await axiosInstance.post(`/api/lms/courses/${courseId}/ai-instructor/custom-script`, {
        script_type: customScriptDialog.scriptType,
        script_text: customScriptForm.script_text,
        generate_audio: customScriptForm.generate_audio,
        voice_type: aiInstructorSettings.voice_type,
        lang_type: aiInstructorSettings.language
      });
      if (response.data.success) {
        showSnackbar('Custom script saved successfully', 'success');
        setCustomScriptDialog({ open: false, scriptType: null });
        setCustomScriptForm({ script_text: '', generate_audio: true });
        // Update local settings
        setAiInstructorSettings(prev => ({
          ...prev,
          custom_scripts: {
            ...prev.custom_scripts,
            [customScriptDialog.scriptType]: customScriptForm.script_text
          }
        }));
      }
    } catch (error) {
      showSnackbar('Error saving custom script', 'error');
    } finally {
      setSavingAISettings(false);
    }
  };

  const getScriptTypeInfo = (type) => {
    const info = {
      course_welcome: { label: 'Course Welcome', icon: <CelebrationIcon />, color: '#a855f7', description: 'Greeting when student starts the course' },
      module_intro: { label: 'Module Introduction', icon: <ModuleIcon />, color: '#3b82f6', description: 'Introduction at the start of each module' },
      module_complete: { label: 'Module Complete', icon: <CheckIcon />, color: '#10b981', description: 'Celebration when module is completed' },
      test_intro: { label: 'Test Introduction', icon: <TestIcon />, color: '#f59e0b', description: 'Encouragement before starting a test' },
      test_pass: { label: 'Test Passed', icon: <TrophyIcon />, color: '#10b981', description: 'Celebration when test is passed' },
      test_fail: { label: 'Test Failed', icon: <TipIcon />, color: '#ef4444', description: 'Encouragement when test is failed' },
      course_complete: { label: 'Course Complete', icon: <GraduationIcon />, color: '#8b5cf6', description: 'Congratulations on course completion' },
      encouragement: { label: 'Encouragement', icon: <AnnouncementIcon />, color: '#06b6d4', description: 'Random encouragement messages' }
    };
    return info[type] || { label: type, icon: <VoiceIcon />, color: '#64748b', description: '' };
  };

  // Content Operations
  const handleCreateContent = async () => {
    if (!contentForm.title.trim()) {
      showSnackbar('Content title is required', 'error');
      return;
    }

    if (contentForm.source_type === 'upload' && uploadFile && contentForm.content_type !== 'embed') {
      const fileValidation = validateFileTypeMatch(uploadFile, contentForm.content_type);
      if (!fileValidation.valid) {
        showSnackbar(fileValidation.message, 'error');
        return;
      }
    }
    
    const isCreateMode = contentForm.source_type === 'create';
    const isFileUpload = contentForm.source_type === 'upload' && uploadFile;
    const isUrlSource = contentForm.source_type === 'url' && contentForm.external_url;
    const isEmbed = contentForm.content_type === 'embed';
    
    // Validation based on source type
    if (isCreateMode) {
      if (!contentEditor || contentEditor.isEmpty) {
        showSnackbar('Please enter content in the editor', 'error');
        return;
      }
    } else if (contentForm.source_type === 'upload' && !isEmbed) {
      if (!uploadFile) {
        showSnackbar('Please select a file to upload', 'error');
        return;
      }
    } else if (contentForm.source_type === 'url') {
      if (!contentForm.external_url) {
        showSnackbar('Please enter a valid URL', 'error');
        return;
      }
    }
    
    setSaving(true);
    try {
      if (isCreateMode) {
        // Create content from editor - generate a DOCX file
        const htmlContent = contentEditor.getHTML();
        const filename = generateDynamicFilename(contentForm.title, contentForm.content_type);
        
        // Convert HTML to DOCX blob
        const docxBlob = await convertHtmlToDocxBlob(htmlContent, contentForm.title);
        const file = new File([docxBlob], filename, {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        
        // Upload as multipart/form-data
        const formData = new FormData();
        formData.append('title', contentForm.title);
        formData.append('description', contentForm.description || '');
        formData.append('content_type', contentForm.content_type);
        formData.append('is_required', contentForm.is_required);
        if (contentForm.video_duration) {
          formData.append('video_duration', contentForm.video_duration);
        }
        formData.append('file', file);
        
        await axiosInstance.post(
          `/api/lms/submodules/${contentDialog.submoduleId}/content`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else if (isFileUpload) {
        // File upload - multipart/form-data
        const formData = new FormData();
        formData.append('title', contentForm.title);
        formData.append('description', contentForm.description || '');
        formData.append('content_type', contentForm.content_type);
        formData.append('is_required', contentForm.is_required);
        if (contentForm.video_duration) {
          formData.append('video_duration', contentForm.video_duration);
        }
        formData.append('file', uploadFile);
        
        await axiosInstance.post(
          `/api/lms/submodules/${contentDialog.submoduleId}/content`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else {
        // External URL or embed - JSON request
        const videoInfo = contentForm.content_type === 'video'
          ? parseVideoUrl(contentForm.external_url)
          : null;
        
        const payload = {
          title: contentForm.title,
          description: contentForm.description || '',
          content_type: contentForm.content_type,
          is_required: contentForm.is_required,
          video_duration: contentForm.video_duration || 0,
          file_url: contentForm.external_url || null,
          video_provider: videoInfo?.provider || null,
          embed_code: videoInfo?.embedCode || (isEmbed ? contentForm.external_url : null),
        };
        
        await axiosInstance.post(
          `/api/lms/submodules/${contentDialog.submoduleId}/content`,
          payload,
          { headers: { 'Content-Type': 'application/json' } }
        );
      }
      
      showSnackbar('Content added successfully', 'success');
      setContentDialog({ open: false, submoduleId: null, content: null });
      resetContentFormWithEditor();
      loadModules();
    } catch (error) {
      console.error('Content creation error:', error.response?.data || error);
      showSnackbar(error.response?.data?.message || 'Error adding content', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Update existing content
  const handleUpdateContent = async () => {
    if (!contentForm.title.trim()) {
      showSnackbar('Content title is required', 'error');
      return;
    }
    
    if (!editingContent?.id) {
      showSnackbar('No content selected for editing', 'error');
      return;
    }
    
    const isCreateMode = contentForm.source_type === 'create';
    const isFileUpload = contentForm.source_type === 'upload' && uploadFile;
    const isUrlSource = contentForm.source_type === 'url';
    const isEmbed = contentForm.content_type === 'embed';
    
    // Validation for create mode
    if (isCreateMode && (!contentEditor || contentEditor.isEmpty)) {
      showSnackbar('Please enter content in the editor', 'error');
      return;
    }
    
    setSaving(true);
    try {
      // Determine if we need to upload a new file
      const needsFileUpdate = isCreateMode || isFileUpload;
      
      if (needsFileUpdate) {
        let fileToUpload;
        
        if (isCreateMode) {
          // Generate DOCX from editor content
          const htmlContent = contentEditor.getHTML();
          const filename = generateDynamicFilename(contentForm.title, contentForm.content_type);
          const docxBlob = await convertHtmlToDocxBlob(htmlContent, contentForm.title);
          fileToUpload = new File([docxBlob], filename, {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          });
        } else {
          fileToUpload = uploadFile;
        }
        
        // Upload new file and update content in one request
        const formData = new FormData();
        formData.append('title', contentForm.title);
        formData.append('description', contentForm.description || '');
        formData.append('content_type', contentForm.content_type);
        formData.append('is_required', contentForm.is_required);
        if (contentForm.video_duration) {
          formData.append('video_duration', contentForm.video_duration);
        }
        formData.append('file', fileToUpload);
        
        // Use PUT with multipart/form-data to update with new file
        await axiosInstance.put(
          `/api/lms/content/${editingContent.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else if (isUrlSource) {
        // External URL update
        const videoInfo = contentForm.content_type === 'video'
          ? parseVideoUrl(contentForm.external_url)
          : null;
        
        const payload = {
          title: contentForm.title,
          content_type: contentForm.content_type,
          description: contentForm.description || '',
          is_required: contentForm.is_required,
          video_duration: contentForm.video_duration || 0,
          file_url: contentForm.external_url || null,
          video_provider: videoInfo?.provider || null,
          embed_code: videoInfo?.embedCode || (isEmbed ? contentForm.external_url : null),
        };
        
        await axiosInstance.put(`/api/lms/content/${editingContent.id}`, payload);
      } else {
        // Just updating metadata without changing file
        const updatePayload = {
          title: contentForm.title,
          description: contentForm.description || '',
          is_required: contentForm.is_required,
          video_duration: contentForm.video_duration || 0,
        };
        
        await axiosInstance.put(`/api/lms/content/${editingContent.id}`, updatePayload);
      }
      
      showSnackbar('Content updated successfully', 'success');
      setContentDialog({ open: false, submoduleId: null, content: null });
      resetContentFormWithEditor();
      loadModules();
    } catch (error) {
      console.error('Content update error:', error.response?.data || error);
      showSnackbar(error.response?.data?.message || 'Error updating content', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle source type change
  const handleSourceTypeChange = (newSourceType) => {
    setContentForm(prev => ({
      ...prev,
      source_type: newSourceType,
      external_url: '',
    }));
    setUploadFile(null);
    setVideoPreview(null);
    if (newSourceType !== 'create' && contentEditor) {
      contentEditor.commands.clearContent();
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    try {
      await axiosInstance.delete(`/api/lms/content/${contentId}`);
      showSnackbar('Content deleted successfully', 'success');
      loadModules();
    } catch (error) { showSnackbar('Error deleting content', 'error'); }
  };

  const handleUploadTranscript = async () => {
    if (!transcriptFile) { showSnackbar('Please select a transcript file', 'error'); return; }
    try {
      const formData = new FormData();
      formData.append('transcript_file', transcriptFile);
      await axiosInstance.post(`/api/lms/content/${transcriptDialog.contentId}/transcript`, formData,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      showSnackbar('Transcript uploaded successfully', 'success');
      setTranscriptDialog({ open: false, contentId: null });
      setTranscriptFile(null);
      loadModules();
    } catch (error) { showSnackbar('Error uploading transcript', 'error'); }
  };

  // Training Operations
  const handleToggleTraining = async (contentId, enable) => {
    setTrainingContent(prev => ({ ...prev, [contentId]: 'loading' }));
    try {
      const response = await axiosInstance.post(`/api/lms/content/${contentId}/toggle-training`, { enabled: enable });
      if (response.data.success) {
        showSnackbar(enable ? 'Content trained successfully' : 'Content removed from training', 'success');
        loadModules();
      } else throw new Error(response.data.error || 'Training operation failed');
    } catch (error) {
      showSnackbar(error.response?.data?.error || error.message || 'Training operation failed', 'error');
    } finally { setTrainingContent(prev => ({ ...prev, [contentId]: null })); }
  };

  const handleScrapeContent = async (contentId) => {
    setTrainingContent(prev => ({ ...prev, [contentId]: 'scraping' }));
    try {
      const response = await axiosInstance.post(`/api/lms/content/${contentId}/scrape`);
      if (response.data.success) { showSnackbar('Content extracted successfully', 'success'); loadModules(); }
      else throw new Error(response.data.error || 'Extraction failed');
    } catch (error) { showSnackbar(error.response?.data?.error || 'Extraction failed', 'error'); }
    finally { setTrainingContent(prev => ({ ...prev, [contentId]: null })); }
  };

  const handleTrainContent = async (contentId) => {
    setTrainingContent(prev => ({ ...prev, [contentId]: 'training' }));
    try {
      const response = await axiosInstance.post(`/api/lms/content/${contentId}/train`);
      if (response.data.success) { showSnackbar('Content trained successfully', 'success'); loadModules(); }
      else throw new Error(response.data.error || 'Training failed');
    } catch (error) { showSnackbar(error.response?.data?.error || 'Training failed', 'error'); }
    finally { setTrainingContent(prev => ({ ...prev, [contentId]: null })); }
  };

  const handleUntrainContent = async (contentId) => {
    setTrainingContent(prev => ({ ...prev, [contentId]: 'untraining' }));
    try {
      const response = await axiosInstance.post(`/api/lms/content/${contentId}/untrain`);
      if (response.data.success) { showSnackbar('Content removed from training', 'success'); loadModules(); }
      else throw new Error(response.data.error || 'Untrain failed');
    } catch (error) { showSnackbar(error.response?.data?.error || 'Untrain failed', 'error'); }
    finally { setTrainingContent(prev => ({ ...prev, [contentId]: null })); }
  };

  const handleBulkTrainSubmodule = async (submoduleId) => {
    setBulkTrainingSubmodule(submoduleId);
    try {
      const response = await axiosInstance.post(`/api/lms/submodules/${submoduleId}/train-all`);
      if (response.data.success) {
        const { success_count, error_count, skipped_count } = response.data.data;
        showSnackbar(`Bulk training: ${success_count} succeeded, ${error_count} failed, ${skipped_count} skipped`,
          error_count > 0 ? 'warning' : 'success');
        loadModules();
      }
    } catch (error) { showSnackbar(error.response?.data?.error || 'Bulk training failed', 'error'); }
    finally { setBulkTrainingSubmodule(null); }
  };

  const handleRegenerateSummary = async (contentId) => {
    setTrainingContent(prev => ({ ...prev, [contentId]: 'regenerating' }));
    try {
      const response = await axiosInstance.post(`/api/lms/content/${contentId}/regenerate-summary`);
      if (response.data.success) { showSnackbar('Summary regenerated successfully', 'success'); loadModules(); }
    } catch (error) { showSnackbar(error.response?.data?.error || 'Regeneration failed', 'error'); }
    finally { setTrainingContent(prev => ({ ...prev, [contentId]: null })); }
  };

  const handleUpdateTrainingData = async () => {
    if (!trainingDataDialog.content) return;
    setSaving(true);
    try {
      const response = await axiosInstance.put(`/api/lms/content/${trainingDataDialog.content.id}/training-data`, trainingDataForm);
      if (response.data.success) {
        showSnackbar('Training data updated successfully', 'success');
        setTrainingDataDialog({ open: false, content: null });
        loadModules();
      }
    } catch (error) { showSnackbar(error.response?.data?.error || 'Update failed', 'error'); }
    finally { setSaving(false); }
  };

  const openTrainingDataDialog = (content) => {
    setTrainingDataForm({ page_content: content.page_content || '', summary: content.summary || '', tags: content.tags || '' });
    setTrainingDataDialog({ open: true, content });
    setTrainingMenuAnchor(null);
  };

  // Thumbnail Upload Handlers
  const handleThumbnailSelect = (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showSnackbar('Please upload a valid image file (JPG, PNG, GIF, WEBP)', 'error');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('Image size must be less than 5MB', 'error');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setThumbnailPreview(e.target.result);
    reader.readAsDataURL(file);

    setThumbnailFile(file);
  };

  const uploadThumbnail = async () => {
    if (!thumbnailFile) return courseSettings.thumbnail_url;

    setUploadingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('file', thumbnailFile);

      const response = await axiosInstance.post(
        `/api/lms/courses/${courseId}/thumbnail`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        return response.data.thumbnail_url;
      }
      return null;
    } catch (error) {
      showSnackbar('Error uploading thumbnail', 'error');
      return null;
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleRemoveThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setCourseSettings({ ...courseSettings, thumbnail_url: '' });
  };

  // Course Operations
  const handleUpdateCourseSettings = async () => {
    setSaving(true);
    try {
      // Upload thumbnail first if a new file was selected
      let thumbnailUrl = courseSettings.thumbnail_url;
      if (thumbnailFile) {
        const uploadedUrl = await uploadThumbnail();
        if (uploadedUrl) {
          thumbnailUrl = uploadedUrl;
        }
      }

      // Update course settings
      await axiosInstance.put(`/api/lms/courses/${courseId}`, {
        ...courseSettings,
        thumbnail_url: thumbnailUrl
      });

      showSnackbar('Course settings updated successfully', 'success');
      setSettingsDialog(false);
      setThumbnailFile(null);
      setThumbnailPreview(null);
      loadCourse();
    } catch (error) { showSnackbar('Error updating course settings', 'error'); }
    finally { setSaving(false); }
  };

  const handlePublishCourse = async () => {
    try {
      await axiosInstance.post(`/api/lms/courses/${courseId}/publish`, { publish: true });
      showSnackbar('Course published successfully', 'success');
      loadCourse();
    } catch (error) { showSnackbar(error.response?.data?.message || 'Error publishing course', 'error'); }
  };

  const handleCreateTest = () => {
    navigate(`/chatbot/${chatbotId}/lms/courses/${courseId}/modules/${testDialog.moduleId}/test-builder`);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(modules);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setModules(items);
    try {
      await axiosInstance.put(`/api/lms/courses/${courseId}/modules/reorder`, { order: items.map(m => m.id) });
      showSnackbar('Modules reordered successfully', 'success');
    } catch (error) { showSnackbar('Error reordering modules', 'error'); loadModules(); }
  };

  // Loading State
  if (loading) {
    return (
      <PageContainer>
        <HeaderSection>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: '#e2e8f0' }} />
            <Box>
              <Skeleton variant="text" width={250} height={32} sx={{ bgcolor: '#e2e8f0' }} />
              <Skeleton variant="text" width={180} height={20} sx={{ bgcolor: '#e2e8f0' }} />
            </Box>
          </Box>
        </HeaderSection>
        <ContentArea>
          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} key={i}>
                <Skeleton variant="rounded" height={120} sx={{ bgcolor: '#e2e8f0', borderRadius: '16px' }} />
              </Grid>
            ))}
          </Grid>
        </ContentArea>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <HeaderSection>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ActionIconButton onClick={() => navigate(`/chatbot/${chatbotId}/lms`)} sx={{ border: '1px solid #e2e8f0' }}>
              <BackIcon />
            </ActionIconButton>
            <Box>
              <Breadcrumbs separator={<BreadcrumbIcon sx={{ fontSize: 16, color: '#94a3b8' }} />} sx={{ mb: 0.5 }}>
                <Link component="button" onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}
                  sx={{ color: '#64748b', fontSize: '13px', textDecoration: 'none', '&:hover': { color: '#4f46e5' } }}>
                  Learning Management
                </Link>
                <Link component="button" sx={{ color: '#64748b', fontSize: '13px', textDecoration: 'none' }}>Courses</Link>
                <Typography sx={{ color: '#1e293b', fontSize: '13px', fontWeight: 500 }}>{course?.title || 'Course Builder'}</Typography>
              </Breadcrumbs>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.5px', display: 'flex', alignItems: 'center', gap: 2 }}>
                {course?.title || 'Course Builder'}
                <StatusChip status={course?.status || 'draft'} label={course?.status === 'published' ? 'Published' : 'Draft'} />
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={2}>
            <SecondaryButton startIcon={<SettingsIcon />} onClick={() => setSettingsDialog(true)}>Settings</SecondaryButton>
            <SecondaryButton startIcon={<PreviewIcon />} onClick={() => navigate(`/chatbot/${chatbotId}/lms/courses/${courseId}/preview`)}>Preview</SecondaryButton>
            {course?.status === 'draft' && <SuccessButton startIcon={<PublishIcon />} onClick={handlePublishCourse}>Publish Course</SuccessButton>}
          </Stack>
        </Box>
      </HeaderSection>

      <ContentArea>
        {/* Stats Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={2.4}>
            <StatCard>
              <IconWrapper color="primary"><ModuleIcon /></IconWrapper>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>{modules.length}</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>Total Modules</Typography>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard>
              <IconWrapper color="success"><PublishedIcon /></IconWrapper>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>{modules.filter(m => m.status === 'published').length}</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>Published</Typography>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard>
              <IconWrapper color="training"><TrainingIcon /></IconWrapper>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>{countTrainedContent().trained}/{countTrainedContent().total}</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>AI Trained</Typography>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard>
              <IconWrapper color="documents"><LibraryBooksIcon /></IconWrapper>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>{supportingDocuments.length}</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>Documents</Typography>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>Completion</Typography>
                <Typography variant="body2" sx={{ color: '#4f46e5', fontWeight: 700 }}>{calculateProgress()}%</Typography>
              </Box>
              <ProgressBar variant="determinate" value={calculateProgress()} />
            </StatCard>
          </Grid>
        </Grid>

        {/* Tabs */}
        <GlassCard sx={{ mb: 3 }}>
          <StyledTabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: '1px solid #f1f5f9' }}>
            <StyledTab label="Course Structure" icon={<ModuleIcon sx={{ fontSize: 20 }} />} iconPosition="start" />
            <StyledTab label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Supporting Documents
                {supportingDocuments.length > 0 && (
                  <Chip size="small" label={supportingDocuments.length} sx={{ height: 20, fontSize: '11px', bgcolor: '#eef2ff', color: '#4f46e5', fontWeight: 600 }} />
                )}
              </Box>
            } icon={<LibraryBooksIcon sx={{ fontSize: 20 }} />} iconPosition="start" />
            <StyledTab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  AI Instructor
                  {aiInstructorSettings.enabled && (
                    <Chip size="small" label="Active" sx={{ height: 20, fontSize: '11px', bgcolor: '#f0fdf4', color: '#059669', fontWeight: 600 }} />
                  )}
                </Box>
              } 
              icon={<RobotIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start" 
            />
          </StyledTabs>
        </GlassCard>

        {/* Course Structure Tab */}
        {activeTab === 0 && (
          <>
            <GlassCard sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>Course Structure</Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>Build your course by adding modules, submodules, and content</Typography>
                </Box>
                <PrimaryButton startIcon={<AddIcon />} onClick={() => {
                  setModuleForm({ title: '', description: '', sort_order: modules.length, requires_knowledge_test: true, min_passing_score: 70 });
                  setModuleDialog({ open: true, module: null, editMode: false });
                }}>Add Module</PrimaryButton>
              </Box>
            </GlassCard>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="modules">
                {(provided) => (
                  <Box {...provided.droppableProps} ref={provided.innerRef}>
                    {modules.length === 0 ? (
                      <GlassCard sx={{ p: 6, textAlign: 'center' }}>
                        <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                          <ModuleIcon sx={{ fontSize: 40, color: '#4f46e5' }} />
                        </Box>
                        <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 600, mb: 1 }}>No modules yet</Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Start building your course by adding the first module</Typography>
                        <PrimaryButton startIcon={<AddIcon />} onClick={() => {
                          setModuleForm({ title: '', description: '', sort_order: 0, requires_knowledge_test: true, min_passing_score: 70 });
                          setModuleDialog({ open: true, module: null, editMode: false });
                        }}>Create First Module</PrimaryButton>
                      </GlassCard>
                    ) : (
                      modules.map((module, index) => (
                        <Draggable key={module.id} draggableId={String(module.id)} index={index}>
                          {(provided, snapshot) => (
                            <ModuleCard ref={provided.innerRef} {...provided.draggableProps} isExpanded={expandedModules[module.id]} status={module.status}
                              sx={{ animation: `${fadeInUp} 0.3s ease forwards`, animationDelay: `${index * 0.05}s`, opacity: 0, ...(snapshot.isDragging && { boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.15)' }) }}>
                              <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', '&:hover': { background: '#fafafa' } }} onClick={() => toggleModuleExpand(module.id)}>
                                <DragHandle {...provided.dragHandleProps}><DragIcon /></DragHandle>
                                <ModuleNumber>{index + 1}</ModuleNumber>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '16px' }}>{module.title}</Typography>
                                    <StatusChip status={module.status} label={module.status} size="small" />
                                    {module.requires_knowledge_test && <FeatureChip variant="test" label="Knowledge Test" size="small" icon={<TestIcon sx={{ fontSize: '14px !important' }} />} />}
                                  </Box>
                                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                                    {module.submodules?.length || 0} submodules • {module.submodules?.reduce((acc, s) => acc + (s.content_items?.length || 0), 0) || 0} content items
                                  </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
                                  {module.status !== 'published' && (
                                    <OutlineButton color="success" size="small" startIcon={publishingModuleId === module.id ? <CircularProgress size={14} color="inherit" /> : <PublishIcon />}
                                      onClick={() => handlePublishModule(module.id)} disabled={publishingModuleId === module.id}>Publish</OutlineButton>
                                  )}
                                  <ActionIconButton onClick={() => {
                                    setModuleForm({ title: module.title, description: module.description || '', sort_order: module.sort_order, requires_knowledge_test: module.requires_knowledge_test, min_passing_score: module.min_passing_score || 70 });
                                    setModuleDialog({ open: true, module: module, editMode: true });
                                  }}><EditIcon fontSize="small" /></ActionIconButton>
                                  <ActionIconButton variant="danger" onClick={() => handleDeleteModule(module.id)}><DeleteIcon fontSize="small" /></ActionIconButton>
                                </Stack>
                                <IconButton sx={{ color: '#64748b' }} onClick={(e) => { e.stopPropagation(); toggleModuleExpand(module.id); }}>
                                  {expandedModules[module.id] ? <CollapseIcon /> : <ExpandIcon />}
                                </IconButton>
                              </Box>
                              <Collapse in={expandedModules[module.id]}>
                                <Box sx={{ px: 2.5, pb: 2.5 }}>
                                  <Divider sx={{ borderColor: '#f1f5f9', mb: 2.5 }} />
                                  {module.description && <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5, pl: 7 }}>{module.description}</Typography>}
                                  {module.requires_knowledge_test && (
                                    <Box sx={{ pl: 7, mb: 2.5 }}>
                                      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)', borderRadius: '12px', border: '1px solid #fde047' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                          <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                            <TestIcon sx={{ color: '#d97706', fontSize: 20 }} />
                                          </Box>
                                          <Box>
                                            <Typography variant="subtitle2" sx={{ color: '#92400e', fontWeight: 600 }}>Knowledge Test</Typography>
                                            <Typography variant="caption" sx={{ color: '#b45309' }}>Passing score: {module.min_passing_score}%</Typography>
                                          </Box>
                                        </Box>
                                        <OutlineButton size="small" onClick={() => setTestDialog({ open: true, moduleId: module.id })}
                                          sx={{ borderColor: '#fcd34d', color: '#92400e', '&:hover': { background: '#fef9c3' } }}>
                                          {module.knowledge_test ? 'Edit Test' : 'Create Test'}
                                        </OutlineButton>
                                      </Box>
                                    </Box>
                                  )}
                                  <Box sx={{ pl: 7 }}>
                                    <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Submodules</Typography>
                                    {module.submodules && module.submodules.length > 0 ? module.submodules.map((submodule) => (
                                      <SubmoduleCard key={submodule.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <SubmoduleIcon sx={{ color: '#4f46e5', fontSize: 20 }} />
                                            <Box>
                                              <Typography variant="subtitle2" sx={{ color: '#0f172a', fontWeight: 600 }}>{submodule.title}</Typography>
                                              {submodule.description && <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px', mt: 0.5 }}>{submodule.description}</Typography>}
                                            </Box>
                                          </Box>
                                          <Stack direction="row" spacing={0.5} alignItems="center">
                                            {submodule.is_training_enabled && <FeatureChip variant="ai" label="AI Training" size="small" icon={<AIIcon sx={{ fontSize: '14px !important' }} />} />}
                                            <ActionIconButton size="small" onClick={() => {
                                              setSubmoduleForm({ title: submodule.title, description: submodule.description || '', is_training_enabled: submodule.is_training_enabled || false });
                                              setSubmoduleDialog({ open: true, moduleId: module.id, submodule: submodule });
                                            }}><EditIcon sx={{ fontSize: 18 }} /></ActionIconButton>
                                            <ActionIconButton size="small" variant="danger" onClick={() => handleDeleteSubmodule(submodule.id)}><DeleteIcon sx={{ fontSize: 18 }} /></ActionIconButton>
                                          </Stack>
                                        </Box>
                                        {submodule.content_items && submodule.content_items.length > 0 && (
                                          <Box sx={{ mt: 2 }}>
                                            {submodule.content_items.map((content) => (
                                              <ContentItem key={content.id} isTrained={content.is_trained}>
                                                <ListItemIcon sx={{ minWidth: 40 }}>{getContentIcon(content.content_type)}</ListItemIcon>
                                                <ListItemText
                                                  primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 500 }}>{content.title}</Typography>
                                                      {submodule.is_training_enabled && (
                                                        <TrainingStatusChip size="small" status={getTrainingStatus(content)} label={getTrainingStatusLabel(content)}
                                                          icon={content.is_trained ? <SuccessIcon /> : content.is_scraped ? <ContentIcon /> : content.training_error ? <ErrorIcon /> : <PendingIcon />} />
                                                      )}
                                                    </Box>
                                                  }
                                                  secondary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'capitalize' }}>{content.content_type}</Typography>
                                                      {content.is_required && <FeatureChip variant="required" label="Required" size="small" sx={{ height: 18, fontSize: '10px' }} />}
                                                      {content.tokens && <Typography variant="caption" sx={{ color: '#94a3b8' }}>• {content.tokens} tokens</Typography>}
                                                    </Box>
                                                  }
                                                />
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                  {submodule.is_training_enabled && (
                                                    <>
                                                      <Tooltip title={!canTrain(content, submodule) ? (content.content_type === 'video' ? 'Upload transcript first' : 'No file attached') : content.is_trained ? 'Remove from training' : 'Train this content'}>
                                                        <span>
                                                          <TrainingButton size="small" trained={content.is_trained} disabled={!canTrain(content, submodule) || trainingContent[content.id]}
                                                            onClick={() => handleToggleTraining(content.id, !content.is_trained)}
                                                            startIcon={trainingContent[content.id] ? <SpinningIcon sx={{ fontSize: 14 }} /> : content.is_trained ? <SuccessIcon sx={{ fontSize: 14 }} /> : <TrainIcon sx={{ fontSize: 14 }} />}>
                                                            {trainingContent[content.id] ? 'Processing...' : content.is_trained ? 'Trained' : 'Train'}
                                                          </TrainingButton>
                                                        </span>
                                                      </Tooltip>
                                                      <ActionIconButton size="small" onClick={(e) => { setTrainingMenuAnchor(e.currentTarget); setSelectedContentForMenu(content); }}>
                                                        <MoreIcon sx={{ fontSize: 18 }} />
                                                      </ActionIconButton>
                                                    </>
                                                  )}
                                                  {content.content_type === 'video' && (
                                                    <Tooltip title={content.has_transcript ? 'Transcript uploaded' : 'Upload transcript'}>
                                                      <ActionIconButton size="small" onClick={() => setTranscriptDialog({ open: true, contentId: content.id })}
                                                        sx={{ color: content.has_transcript ? '#059669' : '#64748b' }}>
                                                        <TranscriptIcon sx={{ fontSize: 18 }} />
                                                      </ActionIconButton>
                                                    </Tooltip>
                                                  )}
                                                  <ActionIconButton 
                                                    size="small" 
                                                    onClick={() => {
                                                      loadContentForEditing(content);
                                                      setContentDialog({ 
                                                        open: true, 
                                                        submoduleId: submodule.id, 
                                                        content: content 
                                                      });
                                                    }}
                                                  >
                                                    <EditIcon sx={{ fontSize: 18 }} />
                                                  </ActionIconButton>
                                                  <ActionIconButton size="small" variant="danger" onClick={() => handleDeleteContent(content.id)}><DeleteIcon sx={{ fontSize: 18 }} /></ActionIconButton>
                                                </Stack>
                                              </ContentItem>
                                            ))}
                                          </Box>
                                        )}
                                        {submodule.is_training_enabled && submodule.content_items?.length > 0 && (
                                          <TrainingInfoBox>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <TrainingIcon sx={{ color: '#15803d', fontSize: 20 }} />
                                                <Box>
                                                  <Typography variant="body2" sx={{ color: '#166534', fontWeight: 600 }}>AI Training Enabled</Typography>
                                                  <Typography variant="caption" sx={{ color: '#15803d' }}>
                                                    {submodule.content_items.filter(c => c.is_trained).length} of {submodule.content_items.length} items trained
                                                  </Typography>
                                                </Box>
                                              </Box>
                                              <TrainingButton trained={false} size="small" disabled={bulkTrainingSubmodule === submodule.id} onClick={() => handleBulkTrainSubmodule(submodule.id)}
                                                startIcon={bulkTrainingSubmodule === submodule.id ? <SpinningIcon sx={{ fontSize: 14 }} /> : <TrainIcon sx={{ fontSize: 14 }} />}
                                                sx={{ background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)', '&:hover': { background: 'linear-gradient(135deg, #166534 0%, #14532d 100%)' } }}>
                                                {bulkTrainingSubmodule === submodule.id ? 'Training All...' : 'Train All'}
                                              </TrainingButton>
                                            </Box>
                                          </TrainingInfoBox>
                                        )}
                                        <Button
                                            size="small"
                                            startIcon={<UploadIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => {
                                              resetContentFormWithEditor();
                                              setContentDialog({ open: true, submoduleId: submodule.id, content: null });
                                            }}
                                            sx={{ mt: 1.5, color: '#4f46e5', fontSize: '13px', textTransform: 'none', '&:hover': { background: '#eef2ff' } }}
                                          >
                                            Add Content
                                          </Button>
                                      </SubmoduleCard>
                                    )) : (
                                      <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8', background: '#fafafa', borderRadius: '12px', border: '1px dashed #e2e8f0' }}>
                                        <SubmoduleIcon sx={{ fontSize: 36, mb: 1, opacity: 0.5 }} />
                                        <Typography variant="body2">No submodules yet</Typography>
                                      </Box>
                                    )}
                                    <Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={() => {
                                      setSubmoduleForm({ title: '', description: '', is_training_enabled: false });
                                      setSubmoduleDialog({ open: true, moduleId: module.id, submodule: null });
                                    }} sx={{ mt: 2, borderColor: '#e2e8f0', color: '#64748b', borderStyle: 'dashed', borderRadius: '10px', py: 1.5, textTransform: 'none',
                                      '&:hover': { borderColor: '#a5b4fc', background: '#eef2ff', color: '#4f46e5' } }}>
                                      Add Submodule
                                    </Button>
                                  </Box>
                                </Box>
                              </Collapse>
                            </ModuleCard>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
        {/* Supporting Documents Tab */}
        {activeTab === 1 && (
          <>
            <GlassCard sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>Supporting Documents</Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Upload course materials, handbooks, templates, and reference documents. Optionally train them for AI chatbot integration.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                  {supportingDocuments.length > 0 && supportingDocuments.some(d => d.is_training_enabled && !d.is_trained) && (
                    <OutlineButton color="success" startIcon={bulkTrainingDocuments ? <SpinningIcon /> : <TrainIcon />} onClick={handleBulkTrainDocuments} disabled={bulkTrainingDocuments}>
                      {bulkTrainingDocuments ? 'Training...' : 'Train All Documents'}
                    </OutlineButton>
                  )}
                  <PrimaryButton startIcon={<UploadIcon />} onClick={() => {
                    setSupportingDocForm({ title: '', description: '', document_type: 'reference', is_downloadable: true, is_visible: true, is_training_enabled: false, access_type: 'enrolled' });
                    setSupportingDocFile(null);
                    setSupportingDocDialog({ open: true, document: null, editMode: false });
                  }}>Upload Document</PrimaryButton>
                </Stack>
              </Box>
            </GlassCard>

            {supportingDocuments.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <Box sx={{ p: 2, borderRadius: '12px', background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', border: '1px solid #93c5fd' }}>
                    <Typography variant="body2" sx={{ color: '#1d4ed8', fontWeight: 600 }}>Total Documents</Typography>
                    <Typography variant="h5" sx={{ color: '#1e40af', fontWeight: 700 }}>{supportingDocuments.length}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ p: 2, borderRadius: '12px', background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', border: '1px solid #6ee7b7' }}>
                    <Typography variant="body2" sx={{ color: '#047857', fontWeight: 600 }}>AI Trained</Typography>
                    <Typography variant="h5" sx={{ color: '#065f46', fontWeight: 700 }}>{countTrainedDocuments().trained} / {countTrainedDocuments().total}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ p: 2, borderRadius: '12px', background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', border: '1px solid #f9a8d4' }}>
                    <Typography variant="body2" sx={{ color: '#be185d', fontWeight: 600 }}>Downloadable</Typography>
                    <Typography variant="h5" sx={{ color: '#9d174d', fontWeight: 700 }}>{supportingDocuments.filter(d => d.is_downloadable).length}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ p: 2, borderRadius: '12px', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '1px solid #fcd34d' }}>
                    <Typography variant="body2" sx={{ color: '#b45309', fontWeight: 600 }}>Hidden</Typography>
                    <Typography variant="h5" sx={{ color: '#92400e', fontWeight: 700 }}>{supportingDocuments.filter(d => !d.is_visible).length}</Typography>
                  </Box>
                </Grid>
              </Grid>
            )}

            {loadingDocuments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#4f46e5' }} /></Box>
            ) : supportingDocuments.length === 0 ? (
              <GlassCard sx={{ p: 6, textAlign: 'center' }}>
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                  <LibraryBooksIcon sx={{ fontSize: 40, color: '#a855f7' }} />
                </Box>
                <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 600, mb: 1 }}>No supporting documents yet</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Upload handbooks, reference guides, templates, or policy documents for your learners</Typography>
                <PrimaryButton startIcon={<UploadIcon />} onClick={() => {
                  setSupportingDocForm({ title: '', description: '', document_type: 'reference', is_downloadable: true, is_visible: true, is_training_enabled: false, access_type: 'enrolled' });
                  setSupportingDocFile(null);
                  setSupportingDocDialog({ open: true, document: null, editMode: false });
                }}>Upload First Document</PrimaryButton>
              </GlassCard>
            ) : (
              <Box>
                {supportingDocuments.map((doc, index) => (
                  <SupportingDocumentItem key={doc.id} isTrained={doc.is_trained} isVisible={doc.is_visible}
                    sx={{ animation: `${fadeInUp} 0.3s ease forwards`, animationDelay: `${index * 0.05}s` }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ width: 48, height: 48, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', flexShrink: 0 }}>
                        {getDocumentIcon(doc.document_type)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>{doc.title}</Typography>
                          <DocumentTypeChip docType={doc.document_type} label={doc.document_type} size="small" />
                          {doc.is_training_enabled && (
                            <TrainingStatusChip size="small" status={getTrainingStatus(doc)} label={getTrainingStatusLabel(doc)}
                              icon={doc.is_trained ? <SuccessIcon /> : doc.is_scraped ? <ContentIcon /> : doc.training_error ? <ErrorIcon /> : <PendingIcon />} />
                          )}
                          {!doc.is_visible && <Chip size="small" icon={<VisibilityOffIcon sx={{ fontSize: '14px !important' }} />} label="Hidden" sx={{ height: 22, fontSize: '10px', bgcolor: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d' }} />}
                        </Box>
                        {doc.description && <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>{doc.description}</Typography>}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>{doc.file_name}</Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>{formatFileSize(doc.file_size)}</Typography>
                          {doc.is_downloadable && <Chip size="small" icon={<DownloadIcon sx={{ fontSize: '12px !important' }} />} label="Downloadable" sx={{ height: 18, fontSize: '10px', bgcolor: '#f1f5f9', color: '#64748b' }} />}
                          {doc.tokens && <Typography variant="caption" sx={{ color: '#94a3b8' }}>{doc.tokens} tokens</Typography>}
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>{doc.view_count || 0} views • {doc.download_count || 0} downloads</Typography>
                        </Box>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {doc.is_training_enabled && (
                          <>
                            <Tooltip title={!doc.file_url ? 'No file attached' : doc.is_trained ? 'Remove from training' : 'Train this document'}>
                              <span>
                                <TrainingButton size="small" trained={doc.is_trained} disabled={!canTrainDocument(doc) || trainingDocuments[doc.id]}
                                  onClick={() => handleToggleDocumentTraining(doc.id, !doc.is_trained)}
                                  startIcon={trainingDocuments[doc.id] ? <SpinningIcon sx={{ fontSize: 14 }} /> : doc.is_trained ? <SuccessIcon sx={{ fontSize: 14 }} /> : <TrainIcon sx={{ fontSize: 14 }} />}>
                                  {trainingDocuments[doc.id] ? 'Processing...' : doc.is_trained ? 'Trained' : 'Train'}
                                </TrainingButton>
                              </span>
                            </Tooltip>
                            <ActionIconButton size="small" onClick={(e) => { setDocumentMenuAnchor(e.currentTarget); setSelectedDocumentForMenu(doc); }}>
                              <MoreIcon sx={{ fontSize: 18 }} />
                            </ActionIconButton>
                          </>
                        )}
                        {doc.file_url && (
                          <Tooltip title="View file">
                            <ActionIconButton size="small" onClick={() => window.open(doc.file_url, '_blank')}><OpenInNewIcon sx={{ fontSize: 18 }} /></ActionIconButton>
                          </Tooltip>
                        )}
                        <ActionIconButton size="small" onClick={() => {
                          setSupportingDocForm({ title: doc.title, description: doc.description || '', document_type: doc.document_type, is_downloadable: doc.is_downloadable, is_visible: doc.is_visible, is_training_enabled: doc.is_training_enabled, access_type: doc.access_type || 'enrolled' });
                          setSupportingDocDialog({ open: true, document: doc, editMode: true });
                        }}><EditIcon sx={{ fontSize: 18 }} /></ActionIconButton>
                        <ActionIconButton size="small" variant="danger" onClick={() => handleDeleteSupportingDocument(doc.id)}><DeleteIcon sx={{ fontSize: 18 }} /></ActionIconButton>
                      </Stack>
                    </Box>
                    {doc.is_training_enabled && (
                      <Box sx={{ mt: 2, p: 2, borderRadius: '8px', background: doc.is_trained ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' : 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', border: doc.is_trained ? '1px solid #bbf7d0' : '1px solid #c7d2fe' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {doc.is_trained ? <SuccessIcon sx={{ color: '#15803d', fontSize: 18 }} /> : <AIIcon sx={{ color: '#4f46e5', fontSize: 18 }} />}
                          <Box>
                            <Typography variant="body2" sx={{ color: doc.is_trained ? '#166534' : '#3730a3', fontWeight: 600 }}>
                              {doc.is_trained ? 'Document trained for AI' : 'AI Training enabled'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: doc.is_trained ? '#15803d' : '#6366f1' }}>
                              {doc.is_trained ? `Last trained: ${new Date(doc.last_trained_at).toLocaleDateString()}` : 'Click "Train" to add this document to your chatbots knowledge base'}
                            </Typography>
                          </Box>
                        </Box>
                        {doc.training_error && (
                          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '6px', background: '#fef2f2', border: '1px solid #fecaca' }}>
                            <Typography variant="caption" sx={{ color: '#dc2626' }}><ErrorIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />{doc.training_error}</Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </SupportingDocumentItem>
                ))}
              </Box>
            )}
          </>
        )}
        {/* AI Instructor Tab */}
        {activeTab === 2 && (
          <>
            <GlassCard sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                    AI Instructor Configuration
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Configure your AI instructor to provide personalized voice guidance throughout the course.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                  <SecondaryButton 
                    startIcon={<PreviewIcon />} 
                    onClick={() => handlePreviewScript('course_welcome')}
                  >
                    Preview
                  </SecondaryButton>
                  <PrimaryButton 
                    startIcon={<SettingsIcon />} 
                    onClick={() => setAiInstructorDialog(true)}
                  >
                    Configure
                  </PrimaryButton>
                </Stack>
              </Box>
            </GlassCard>

            {loadingAISettings ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#a855f7' }} />
              </Box>
            ) : (
              <>
                {/* AI Instructor Status Card */}
                <AIInstructorCard isEnabled={aiInstructorSettings.enabled} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        width: 64, height: 64, borderRadius: '50%', 
                        background: aiInstructorSettings.enabled 
                          ? 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)'
                          : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: aiInstructorSettings.enabled 
                          ? '0 4px 14px rgba(168, 85, 247, 0.4)'
                          : '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}>
                        <RobotIcon sx={{ fontSize: 32, color: '#fff' }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {aiInstructorSettings.instructor_name || 'AI Instructor'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {aiInstructorSettings.enabled ? 'Active and ready to guide learners' : 'Currently disabled'}
                        </Typography>
                      </Box>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={aiInstructorSettings.enabled}
                          onChange={(e) => setAiInstructorSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#a855f7' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#c084fc' }
                          }}
                        />
                      }
                      label=""
                    />
                  </Box>

                  {aiInstructorSettings.enabled && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ p: 2, borderRadius: '12px', background: '#fff', border: '1px solid #e9d5ff' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            {aiInstructorSettings.voice_type === 'FEMALE' ? (
                              <FemaleIcon sx={{ color: '#ec4899', fontSize: 20 }} />
                            ) : (
                              <MaleIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                            )}
                            <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600 }}>Voice Type</Typography>
                          </Box>
                          <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700 }}>
                            {aiInstructorSettings.voice_type === 'FEMALE' ? 'Female Voice' : 'Male Voice'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ p: 2, borderRadius: '12px', background: '#fff', border: '1px solid #e9d5ff' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <LanguageIcon sx={{ color: '#a855f7', fontSize: 20 }} />
                            <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600 }}>Language</Typography>
                          </Box>
                          <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700 }}>
                            {aiInstructorSettings.language === 'en' ? 'English' : aiInstructorSettings.language.toUpperCase()}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ p: 2, borderRadius: '12px', background: '#fff', border: '1px solid #e9d5ff' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <AudioIcon sx={{ color: '#a855f7', fontSize: 20 }} />
                            <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600 }}>Active Scripts</Typography>
                          </Box>
                          <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700 }}>
                            {Object.values(aiInstructorSettings.features || {}).filter(Boolean).length} of 8
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </AIInstructorCard>

                {/* Script Types Section */}
                {aiInstructorSettings.enabled && (
                  <GlassCard sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                      Voice Scripts
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                      Configure what your AI instructor says at different points in the course.
                    </Typography>

                    <Grid container spacing={2}>
                      {Object.entries(aiInstructorSettings.features || {}).map(([scriptType, isEnabled]) => {
                        const info = getScriptTypeInfo(scriptType);
                        const hasCustomScript = aiInstructorSettings.custom_scripts?.[scriptType];
                        return (
                          <Grid item xs={12} md={6} lg={4} key={scriptType}>
                            <ScriptTypeCard isActive={isEnabled}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Box sx={{ 
                                    width: 40, height: 40, borderRadius: '10px',
                                    background: isEnabled ? `${info.color}15` : '#f1f5f9',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                  }}>
                                    {React.cloneElement(info.icon, { 
                                      sx: { color: isEnabled ? info.color : '#94a3b8', fontSize: 20 } 
                                    })}
                                  </Box>
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="subtitle2" sx={{ 
                                        color: isEnabled ? '#0f172a' : '#64748b', 
                                        fontWeight: 600 
                                      }}>
                                        {info.label}
                                      </Typography>
                                      {hasCustomScript && (
                                        <Chip 
                                          size="small" 
                                          label="Custom" 
                                          sx={{ 
                                            height: 18, fontSize: '10px', 
                                            bgcolor: '#fef3c7', color: '#b45309',
                                            fontWeight: 600
                                          }} 
                                        />
                                      )}
                                    </Box>
                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                      {info.description}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Switch
                                  size="small"
                                  checked={isEnabled}
                                  onChange={(e) => setAiInstructorSettings(prev => ({
                                    ...prev,
                                    features: { ...prev.features, [scriptType]: e.target.checked }
                                  }))}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: info.color },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: info.color }
                                  }}
                                />
                              </Box>
                              {isEnabled && (
                                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    startIcon={<PlayIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => handlePreviewScript(scriptType)}
                                    sx={{ 
                                      color: info.color, fontSize: '12px', textTransform: 'none',
                                      '&:hover': { background: `${info.color}10` }
                                    }}
                                  >
                                    Preview
                                  </Button>
                                  <Button
                                    size="small"
                                    startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => {
                                      setCustomScriptForm({
                                        script_text: aiInstructorSettings.custom_scripts?.[scriptType] || '',
                                        generate_audio: true
                                      });
                                      setCustomScriptDialog({ open: true, scriptType });
                                    }}
                                    sx={{ 
                                      color: '#64748b', fontSize: '12px', textTransform: 'none',
                                      '&:hover': { background: '#f1f5f9' }
                                    }}
                                  >
                                    Customize
                                  </Button>
                                </Box>
                              )}
                            </ScriptTypeCard>
                          </Grid>
                        );
                      })}
                    </Grid>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <PrimaryButton
                        onClick={handleSaveAIInstructorSettings}
                        disabled={savingAISettings}
                        startIcon={savingAISettings ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      >
                        {savingAISettings ? 'Saving...' : 'Save Settings'}
                      </PrimaryButton>
                    </Box>
                  </GlassCard>
                )}
              </>
            )}
          </>
        )}
      </ContentArea>

      {/* Training Menu for Content Items */}
      <Menu anchorEl={trainingMenuAnchor} open={Boolean(trainingMenuAnchor)} onClose={() => { setTrainingMenuAnchor(null); setSelectedContentForMenu(null); }}
        PaperProps={{ sx: { bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', minWidth: 220 } }}>
        <MenuItem onClick={() => { handleScrapeContent(selectedContentForMenu?.id); setTrainingMenuAnchor(null); }} disabled={trainingContent[selectedContentForMenu?.id]} sx={{ py: 1.5 }}>
          <ListItemIcon><ContentIcon sx={{ color: '#4f46e5', fontSize: 20 }} /></ListItemIcon>
          <ListItemText primary="Extract Content" secondary="Scrape text from file" />
        </MenuItem>
        <MenuItem onClick={() => { handleTrainContent(selectedContentForMenu?.id); setTrainingMenuAnchor(null); }} disabled={!selectedContentForMenu?.is_scraped || trainingContent[selectedContentForMenu?.id]} sx={{ py: 1.5 }}>
          <ListItemIcon><TrainIcon sx={{ color: '#059669', fontSize: 20 }} /></ListItemIcon>
          <ListItemText primary="Train to Chatbot" secondary="Add to vector database" />
        </MenuItem>
        <MenuItem onClick={() => { handleUntrainContent(selectedContentForMenu?.id); setTrainingMenuAnchor(null); }} disabled={!selectedContentForMenu?.is_trained || trainingContent[selectedContentForMenu?.id]} sx={{ py: 1.5 }}>
          <ListItemIcon><UntrainIcon sx={{ color: '#dc2626', fontSize: 20 }} /></ListItemIcon>
          <ListItemText primary="Remove Training" secondary="Remove from chatbot" />
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={() => openTrainingDataDialog(selectedContentForMenu)} disabled={!selectedContentForMenu?.is_scraped} sx={{ py: 1.5 }}>
          <ListItemIcon><EditIcon sx={{ color: '#64748b', fontSize: 20 }} /></ListItemIcon>
          <ListItemText primary="Edit Training Data" secondary="Modify extracted content" />
        </MenuItem>
        <MenuItem onClick={() => { handleRegenerateSummary(selectedContentForMenu?.id); setTrainingMenuAnchor(null); }} disabled={!selectedContentForMenu?.is_scraped || trainingContent[selectedContentForMenu?.id]} sx={{ py: 1.5 }}>
          <ListItemIcon><RefreshIcon sx={{ color: '#6366f1', fontSize: 20 }} /></ListItemIcon>
          <ListItemText primary="Regenerate Summary" secondary="Create new AI summary" />
        </MenuItem>
      </Menu>

      {/* Training Menu for Supporting Documents */}
      <Menu anchorEl={documentMenuAnchor} open={Boolean(documentMenuAnchor)} onClose={() => { setDocumentMenuAnchor(null); setSelectedDocumentForMenu(null); }}
        PaperProps={{ sx: { bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', minWidth: 220 } }}>
        <MenuItem onClick={() => { handleScrapeDocument(selectedDocumentForMenu?.id); setDocumentMenuAnchor(null); }} disabled={trainingDocuments[selectedDocumentForMenu?.id]} sx={{ py: 1.5 }}>
          <ListItemIcon><ContentIcon sx={{ color: '#4f46e5', fontSize: 20 }} /></ListItemIcon>
          <ListItemText primary="Extract Content" secondary="Scrape text from file" />
        </MenuItem>
        <MenuItem onClick={() => { handleTrainDocument(selectedDocumentForMenu?.id); setDocumentMenuAnchor(null); }} disabled={!selectedDocumentForMenu?.is_scraped || trainingDocuments[selectedDocumentForMenu?.id]} sx={{ py: 1.5 }}>
          <ListItemIcon><TrainIcon sx={{ color: '#059669', fontSize: 20 }} /></ListItemIcon>
          <ListItemText primary="Train to Chatbot" secondary="Add to vector database" />
        </MenuItem>
        <MenuItem onClick={() => { handleUntrainDocument(selectedDocumentForMenu?.id); setDocumentMenuAnchor(null); }} disabled={!selectedDocumentForMenu?.is_trained || trainingDocuments[selectedDocumentForMenu?.id]} sx={{ py: 1.5 }}>
          <ListItemIcon><UntrainIcon sx={{ color: '#dc2626', fontSize: 20 }} /></ListItemIcon>
          <ListItemText primary="Remove Training" secondary="Remove from chatbot" />
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={() => openDocumentTrainingDataDialog(selectedDocumentForMenu)} disabled={!selectedDocumentForMenu?.is_scraped} sx={{ py: 1.5 }}>
          <ListItemIcon><EditIcon sx={{ color: '#64748b', fontSize: 20 }} /></ListItemIcon>
          <ListItemText primary="Edit Training Data" secondary="Modify extracted content" />
        </MenuItem>
      </Menu>

      {/* Module Dialog */}
      <StyledDialog open={moduleDialog.open} onClose={() => setModuleDialog({ open: false, module: null, editMode: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper color="primary" sx={{ width: 44, height: 44 }}><ModuleIcon sx={{ fontSize: 22 }} /></IconWrapper>
            {moduleDialog.editMode ? 'Edit Module' : 'Create New Module'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Module Title" placeholder="e.g., Introduction to Safety Protocols" value={moduleForm.title}
                onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Description" placeholder="Describe what learners will achieve..." value={moduleForm.description}
                onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })} multiline rows={3} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2.5, borderRadius: '12px', background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)', border: '1px solid #fde047' }}>
                <FormControlLabel control={<Switch checked={moduleForm.requires_knowledge_test}
                  onChange={(e) => setModuleForm({ ...moduleForm, requires_knowledge_test: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#d97706' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#f59e0b' } }} />}
                  label={<Box><Typography variant="body2" sx={{ color: '#92400e', fontWeight: 600 }}>Require Knowledge Test</Typography>
                    <Typography variant="caption" sx={{ color: '#b45309' }}>Learners must pass a test to complete this module</Typography></Box>} />
              </Box>
            </Grid>
            {moduleForm.requires_knowledge_test && (
              <Grid item xs={12}>
                <StyledTextField fullWidth type="number" label="Minimum Passing Score (%)" value={moduleForm.min_passing_score}
                  onChange={(e) => setModuleForm({ ...moduleForm, min_passing_score: parseFloat(e.target.value) })}
                  inputProps={{ min: 0, max: 100 }} InputProps={{ endAdornment: <InputAdornment position="end" sx={{ color: '#64748b' }}>%</InputAdornment> }} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setModuleDialog({ open: false, module: null, editMode: false })}>Cancel</SecondaryButton>
          <PrimaryButton onClick={moduleDialog.editMode ? handleUpdateModule : handleCreateModule}>{moduleDialog.editMode ? 'Update Module' : 'Create Module'}</PrimaryButton>
        </DialogActions>
      </StyledDialog>

      {/* Submodule Dialog */}
      <StyledDialog open={submoduleDialog.open} onClose={() => setSubmoduleDialog({ open: false, moduleId: null, submodule: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper color="primary" sx={{ width: 44, height: 44 }}><SubmoduleIcon sx={{ fontSize: 22 }} /></IconWrapper>
            {submoduleDialog.submodule ? 'Edit Submodule' : 'Create New Submodule'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Submodule Title" placeholder="e.g., Fire Safety Basics" value={submoduleForm.title}
                onChange={(e) => setSubmoduleForm({ ...submoduleForm, title: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Description" placeholder="Describe the content of this submodule..." value={submoduleForm.description}
                onChange={(e) => setSubmoduleForm({ ...submoduleForm, description: e.target.value })} multiline rows={3} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2.5, borderRadius: '12px', background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', border: '1px solid #c7d2fe' }}>
                <FormControlLabel control={<Switch checked={submoduleForm.is_training_enabled}
                  onChange={(e) => setSubmoduleForm({ ...submoduleForm, is_training_enabled: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4f46e5' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#6366f1' } }} />}
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><AIIcon sx={{ color: '#4f46e5', fontSize: 22 }} />
                    <Box><Typography variant="body2" sx={{ color: '#3730a3', fontWeight: 600 }}>Enable AI Training</Typography>
                      <Typography variant="caption" sx={{ color: '#6366f1' }}>Upload transcripts to train your chatbot with this content</Typography>
                      <Typography variant="caption" sx={{ color: 'red', display: 'block' }}>Beware: disabling training after files are trained will remove them from chatbot data</Typography></Box></Box>} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setSubmoduleDialog({ open: false, moduleId: null, submodule: null })}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submoduleDialog.submodule ? handleUpdateSubmodule : handleCreateSubmodule}>{submoduleDialog.submodule ? 'Update Submodule' : 'Create Submodule'}</PrimaryButton>
        </DialogActions>
      </StyledDialog>

      {/* Content Dialog - Updated with TipTap Editor */}
      <StyledDialog
        open={contentDialog.open}
        onClose={() => {
          setContentDialog({ open: false, submoduleId: null, content: null });
          resetContentFormWithEditor();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper color="primary" sx={{ width: 44, height: 44 }}>
              {editingContent ? <EditIcon sx={{ fontSize: 22 }} /> : <UploadIcon sx={{ fontSize: 22 }} />}
            </IconWrapper>
            {editingContent ? 'Edit Content' : 'Add Content'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Title */}
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Content Title"
                placeholder="e.g., Introduction to Safety Protocols"
                value={contentForm.title}
                onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
                required
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Description"
                placeholder="Brief description of this content..."
                value={contentForm.description}
                onChange={(e) => setContentForm({ ...contentForm, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            {/* Content Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#6366f1' } }}>
                  Content Type
                </InputLabel>
                <StyledSelect
                  value={contentForm.content_type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setContentForm(prev => ({
                      ...prev,
                      content_type: newType,
                      source_type: newType === 'video' ? 'url' : prev.source_type,
                    }));
                    setUploadFile(null);
                    setVideoPreview(null);
                  }}
                  label="Content Type"
                  MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', border: '1px solid #e2e8f0' } } }}
                >
                  <MenuItem value="document">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <DocumentIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                      Document
                    </Box>
                  </MenuItem>
                  <MenuItem value="slide">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <PdfIcon sx={{ color: '#d97706', fontSize: 20 }} />
                      Slide
                    </Box>
                  </MenuItem>
                  <MenuItem value="video">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <VideoIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                      Video
                    </Box>
                  </MenuItem>
                  <MenuItem value="embed">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <FileIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
                      Embedded Content
                    </Box>
                  </MenuItem>
                </StyledSelect>
              </FormControl>
            </Grid>

            {/* Required Toggle */}
            <Grid item xs={12} sm={6}>
              <Box sx={{
                p: 2,
                borderRadius: '10px',
                background: contentForm.is_required ? '#fef2f2' : '#f8fafc',
                border: contentForm.is_required ? '1px solid #fecaca' : '1px solid #e2e8f0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={contentForm.is_required}
                      onChange={(e) => setContentForm({ ...contentForm, is_required: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#dc2626' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ef4444' },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 500 }}>
                      Required Content
                    </Typography>
                  }
                />
              </Box>
            </Grid>
            
            {/* Existing File Info (for Edit Mode) */}
            {editingContent && editingContent.file_url  && (
              <Grid item xs={12}>
                <ExistingFileInfo>
                  <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #bbf7d0' }}>
                    {getContentIcon(editingContent.content_type)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: '#166534', fontWeight: 600 }}>
                      Current File: {editingContent.file_name || 'Uploaded file'}
                    </Typography>
                  </Box>
                  <Tooltip title="View current file">
                    <ActionIconButton size="small" onClick={() => window.open(editingContent.file_url, '_blank')}>
                      <OpenInNewIcon sx={{ fontSize: 18 }} />
                    </ActionIconButton>
                  </Tooltip>
                </ExistingFileInfo>
              </Grid>
            )}
            {/* Source Type Selection - Only for document, slide types (not video) */}
            {['document', 'slide'].includes(contentForm.content_type) && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 2, fontWeight: 600 }}>
                  Content Source
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <SourceTypeCard
                    selected={contentForm.source_type === 'create'}
                    onClick={() => handleSourceTypeChange('create')}
                  >
                    <CreateIcon sx={{ fontSize: 28, color: contentForm.source_type === 'create' ? '#4f46e5' : '#94a3b8', mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ color: contentForm.source_type === 'create' ? '#4f46e5' : '#64748b', fontWeight: 600 }}>
                      Create Content
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      Write using text editor
                    </Typography>
                  </SourceTypeCard>

                  <SourceTypeCard
                    selected={contentForm.source_type === 'upload'}
                    onClick={() => handleSourceTypeChange('upload')}
                  >
                    <UploadIcon sx={{ fontSize: 28, color: contentForm.source_type === 'upload' ? '#4f46e5' : '#94a3b8', mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ color: contentForm.source_type === 'upload' ? '#4f46e5' : '#64748b', fontWeight: 600 }}>
                      Upload File
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      Upload from your device
                    </Typography>
                  </SourceTypeCard>

                  <SourceTypeCard
                    selected={contentForm.source_type === 'url'}
                    onClick={() => handleSourceTypeChange('url')}
                  >
                    <LinkIcon sx={{ fontSize: 28, color: contentForm.source_type === 'url' ? '#4f46e5' : '#94a3b8', mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ color: contentForm.source_type === 'url' ? '#4f46e5' : '#64748b', fontWeight: 600 }}>
                      External URL
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      Link to external file
                    </Typography>
                  </SourceTypeCard>
                </Box>
              </Grid>
            )}

            {/* Video Source Type - Always URL for videos */}
            {contentForm.content_type === 'video' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 2, fontWeight: 600 }}>
                  Content Source
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <SourceTypeCard
                    selected={contentForm.source_type === 'upload'}
                    onClick={() => handleSourceTypeChange('upload')}
                  >
                    <UploadIcon sx={{ fontSize: 28, color: contentForm.source_type === 'upload' ? '#4f46e5' : '#94a3b8', mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ color: contentForm.source_type === 'upload' ? '#4f46e5' : '#64748b', fontWeight: 600 }}>
                      Upload Video
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      Upload from your device
                    </Typography>
                  </SourceTypeCard>

                  <SourceTypeCard
                    selected={contentForm.source_type === 'url'}
                    onClick={() => handleSourceTypeChange('url')}
                  >
                    <LinkIcon sx={{ fontSize: 28, color: contentForm.source_type === 'url' ? '#4f46e5' : '#94a3b8', mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ color: contentForm.source_type === 'url' ? '#4f46e5' : '#64748b', fontWeight: 600 }}>
                      Video URL
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      YouTube, Vimeo, etc.
                    </Typography>
                  </SourceTypeCard>
                </Box>
              </Grid>
            )}


            {/* Create Content - TipTap Editor */}
            {contentForm.source_type === 'create' && ['document', 'slide'].includes(contentForm.content_type) && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1.5, fontWeight: 600 }}>
                  Content Editor <span style={{ color: '#dc2626' }}>*</span>
                </Typography>
                
                {/* Use the same TiptapEditor component as EditDocuments */}
                <Box sx={{ 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '12px', 
                  overflow: 'hidden',
                  '&:focus-within': {
                    borderColor: '#6366f1',
                    boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
                  }
                }}>
                  <TiptapEditor editor={contentEditor} height="300px" />
                </Box>
                
                <Typography variant="caption" sx={{ color: '#94a3b8', mt: 1, display: 'block' }}>
                  A document file will be automatically generated from your content
                </Typography>
              </Grid>
            )}

            {/* File Upload Zone */}
            {contentForm.source_type === 'upload' && contentForm.content_type !== 'embed' && (
            <Grid item xs={12}>
              <FileUploadZone component="label" isDragActive={false} sx={{
                width: '100%',
                display: 'block',
              }}>
                <input
                  type="file"
                  hidden
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  accept={getAcceptedExtensions(contentForm.content_type)}
                />
                <Box sx={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <UploadIcon sx={{ fontSize: 28, color: '#4f46e5' }} />
                </Box>
                <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600, mb: 0.5 }}>
                  {uploadFile ? uploadFile.name : 'Click to upload'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {contentForm.content_type === 'video' 
                    ? `Accepted: ${FILE_TYPE_MAPPINGS.video.join(', ')}`
                    : contentForm.content_type === 'slide' 
                    ? `Accepted: ${FILE_TYPE_MAPPINGS.slide.join(', ')}`
                    : `Accepted: ${FILE_TYPE_MAPPINGS.document.join(', ')}`
                  }
                </Typography>
                {uploadFile && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      label={`Selected: ${uploadFile.name}`}
                      onDelete={() => setUploadFile(null)}
                      sx={{ bgcolor: '#eef2ff', color: '#4f46e5' }}
                    />
                    {getContentTypeFromExtension(uploadFile.name) && 
                    contentForm.content_type !== getContentTypeFromExtension(uploadFile.name) && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#059669', mt: 1 }}>
                        ✓ Content type updated to: {getContentTypeLabel(getContentTypeFromExtension(uploadFile.name))}
                      </Typography>
                    )}
                  </Box>
                )}
              </FileUploadZone>
            </Grid>
          )}

            {/* External URL Input */}
            {contentForm.source_type === 'url' && (
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label={contentForm.content_type === 'video' ? 'Video URL' : 'External File URL'}
                  placeholder={
                    contentForm.content_type === 'video'
                      ? 'https://www.youtube.com/watch?v=... or https://vimeo.com/...'
                      : 'https://example.com/document.pdf'
                  }
                  value={contentForm.external_url}
                  onChange={(e) => {
                    const url = e.target.value;
                    setContentForm({ ...contentForm, external_url: url });
                    if (contentForm.content_type === 'video' && url) {
                      const parsed = parseVideoUrl(url);
                      setVideoPreview(parsed);
                    } else {
                      setVideoPreview(null);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon sx={{ color: '#64748b' }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    contentForm.content_type === 'video'
                      ? 'Supported: YouTube, Vimeo, Dailymotion, Wistia, Loom, or direct video URL'
                      : 'Enter the direct URL to the file'
                  }
                />

                {/* Video Preview */}
                {contentForm.content_type === 'video' && videoPreview && (
                  <Box sx={{ mt: 2 }}>
                    {videoPreview.isValid ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <SuccessIcon sx={{ color: '#10b981', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: '#059669', fontWeight: 500 }}>
                            Valid video URL detected
                          </Typography>
                          <ProviderChip provider={videoPreview.provider} label={videoPreview.provider} size="small" />
                        </Box>
                        <VideoPreviewBox>
                          <iframe
                            src={videoPreview.embedUrl}
                            title="Video Preview"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </VideoPreviewBox>
                      </>
                    ) : contentForm.external_url && (
                      <Box sx={{
                        p: 2, borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca',
                        display: 'flex', alignItems: 'center', gap: 1,
                      }}>
                        <ErrorIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#dc2626' }}>
                          Unable to parse video URL. It will be saved as a direct link.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Grid>
            )}

            {/* Embed Code Input */}
            {contentForm.content_type === 'embed' && (
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Embed Code"
                  placeholder="<iframe src='...' ...></iframe>"
                  value={contentForm.external_url}
                  onChange={(e) => setContentForm({ ...contentForm, external_url: e.target.value })}
                  multiline
                  rows={4}
                  helperText="Paste the embed code from the video platform or other service"
                />
              </Grid>
            )}

            {/* Video Duration */}
            {contentForm.content_type === 'video' && (
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  type="number"
                  label="Video Duration (optional)"
                  value={contentForm.video_duration}
                  onChange={(e) => setContentForm({ ...contentForm, video_duration: parseInt(e.target.value) || 0 })}
                  inputProps={{ min: 0 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end" sx={{ color: '#64748b' }}>minutes</InputAdornment>,
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => {
            setContentDialog({ open: false, submoduleId: null, content: null });
            resetContentFormWithEditor();
          }}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={editingContent ? handleUpdateContent : handleCreateContent}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : editingContent ? (
              'Update Content'
            ) : (
              'Add Content'
            )}
          </PrimaryButton>
        </DialogActions>
      </StyledDialog>

      {/* Supporting Document Dialog */}
      <StyledDialog open={supportingDocDialog.open} onClose={() => { setSupportingDocDialog({ open: false, document: null, editMode: false }); setSupportingDocFile(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper color="documents" sx={{ width: 44, height: 44 }}><LibraryBooksIcon sx={{ fontSize: 22 }} /></IconWrapper>
            {supportingDocDialog.editMode ? 'Edit Supporting Document' : 'Upload Supporting Document'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Document Title" placeholder="e.g., Safety Handbook" value={supportingDocForm.title}
                onChange={(e) => setSupportingDocForm({ ...supportingDocForm, title: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Description" placeholder="Brief description of this document..." value={supportingDocForm.description}
                onChange={(e) => setSupportingDocForm({ ...supportingDocForm, description: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#6366f1' } }}>Document Type</InputLabel>
                <StyledSelect value={supportingDocForm.document_type} onChange={(e) => setSupportingDocForm({ ...supportingDocForm, document_type: e.target.value })} label="Document Type"
                  MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', border: '1px solid #e2e8f0', '& .MuiMenuItem-root': { color: '#1e293b', '&:hover': { bgcolor: '#f8fafc' } } } } }}>
                  <MenuItem value="reference"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><LibraryBooksIcon sx={{ color: '#1d4ed8', fontSize: 20 }} />Reference Guide</Box></MenuItem>
                  <MenuItem value="handbook"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><DescriptionIcon sx={{ color: '#be185d', fontSize: 20 }} />Handbook</Box></MenuItem>
                  <MenuItem value="template"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><FileCopyIcon sx={{ color: '#047857', fontSize: 20 }} />Template</Box></MenuItem>
                  <MenuItem value="policy"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><PolicyIcon sx={{ color: '#b91c1c', fontSize: 20 }} />Policy Document</Box></MenuItem>
                  <MenuItem value="other"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><AttachFileIcon sx={{ color: '#4b5563', fontSize: 20 }} />Other</Box></MenuItem>
                </StyledSelect>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#6366f1' } }}>Access Type</InputLabel>
                <StyledSelect value={supportingDocForm.access_type} onChange={(e) => setSupportingDocForm({ ...supportingDocForm, access_type: e.target.value })} label="Access Type"
                  MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', border: '1px solid #e2e8f0', '& .MuiMenuItem-root': { color: '#1e293b', '&:hover': { bgcolor: '#f8fafc' } } } } }}>
                  <MenuItem value="enrolled">Enrolled Students Only</MenuItem>
                  <MenuItem value="all">All Users</MenuItem>
                </StyledSelect>
              </FormControl>
            </Grid>
            {!supportingDocDialog.editMode && (
              <Grid item xs={12}>
                <FileUploadZone component="label" isDragActive={false}>
                  <input type="file" hidden onChange={(e) => setSupportingDocFile(e.target.files[0])}
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.json,.xml,.md,.html,.png,.jpg,.jpeg,.gif,.webp" />
                  <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <UploadIcon sx={{ fontSize: 28, color: '#a855f7' }} />
                  </Box>
                  <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600, mb: 0.5 }}>{supportingDocFile ? supportingDocFile.name : 'Click to upload or drag and drop'}</Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT, CSV, JSON, XML, MD, HTML, or Images</Typography>
                </FileUploadZone>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ p: 2, borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ color: '#1e293b', fontWeight: 600, mb: 2 }}>Document Settings</Typography>
                <Stack spacing={2}>
                  <FormControlLabel control={<Switch checked={supportingDocForm.is_visible} onChange={(e) => setSupportingDocForm({ ...supportingDocForm, is_visible: e.target.checked })}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4f46e5' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#6366f1' } }} />}
                    label={<Typography variant="body2" sx={{ color: '#1e293b' }}>Visible to students</Typography>} />
                  <FormControlLabel control={<Switch checked={supportingDocForm.is_downloadable} onChange={(e) => setSupportingDocForm({ ...supportingDocForm, is_downloadable: e.target.checked })}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4f46e5' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#6366f1' } }} />}
                    label={<Typography variant="body2" sx={{ color: '#1e293b' }}>Allow download</Typography>} />
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2.5, borderRadius: '12px', background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', border: '1px solid #c7d2fe' }}>
                <FormControlLabel control={<Switch checked={supportingDocForm.is_training_enabled}
                  onChange={(e) => setSupportingDocForm({ ...supportingDocForm, is_training_enabled: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4f46e5' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#6366f1' } }} />}
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><AIIcon sx={{ color: '#4f46e5', fontSize: 22 }} />
                    <Box><Typography variant="body2" sx={{ color: '#3730a3', fontWeight: 600 }}>Enable AI Training</Typography>
                      <Typography variant="caption" sx={{ color: '#6366f1' }}>Extract content and train your chatbot with this document</Typography></Box></Box>} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => { setSupportingDocDialog({ open: false, document: null, editMode: false }); setSupportingDocFile(null); }}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleUploadSupportingDocument} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : supportingDocDialog.editMode ? 'Update Document' : 'Upload Document'}
          </PrimaryButton>
        </DialogActions>
      </StyledDialog>

      {/* Transcript Dialog */}
      <StyledDialog open={transcriptDialog.open} onClose={() => { setTranscriptDialog({ open: false, contentId: null }); setTranscriptFile(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper color="primary" sx={{ width: 44, height: 44 }}><TranscriptIcon sx={{ fontSize: 22 }} /></IconWrapper>
            Upload Transcript
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Upload a transcript file to enable AI training for this content. Supported formats: TXT, VTT, SRT.</Typography>
          <FileUploadZone component="label">
            <input type="file" hidden accept=".txt,.vtt,.srt" onChange={(e) => setTranscriptFile(e.target.files[0])} />
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <TranscriptIcon sx={{ fontSize: 28, color: '#4f46e5' }} />
            </Box>
            <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600, mb: 0.5 }}>{transcriptFile ? transcriptFile.name : 'Click to upload transcript file'}</Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>TXT, VTT, or SRT format</Typography>
          </FileUploadZone>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => { setTranscriptDialog({ open: false, contentId: null }); setTranscriptFile(null); }}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleUploadTranscript}>Upload Transcript</PrimaryButton>
        </DialogActions>
      </StyledDialog>

      {/* Test Dialog */}
      <StyledDialog open={testDialog.open} onClose={() => setTestDialog({ open: false, moduleId: null })} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper color="warning" sx={{ width: 44, height: 44 }}><TestIcon sx={{ fontSize: 22 }} /></IconWrapper>
            Knowledge Test
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Create and manage the knowledge test for this module. Learners must pass this test to complete the module.</Typography>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setTestDialog({ open: false, moduleId: null })}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleCreateTest}>Open Test Builder</PrimaryButton>
        </DialogActions>
      </StyledDialog>

      {/* Training Data Edit Dialog for Content Items */}
      <StyledDialog open={trainingDataDialog.open} onClose={() => setTrainingDataDialog({ open: false, content: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper color="training" sx={{ width: 44, height: 44 }}><EditIcon sx={{ fontSize: 22 }} /></IconWrapper>
            Edit Training Data
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Edit the extracted content, summary, and tags. Changes will be reflected in your chatbot's responses.</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Extracted Content" placeholder="The main text content..." value={trainingDataForm.page_content}
                onChange={(e) => setTrainingDataForm({ ...trainingDataForm, page_content: e.target.value })} multiline rows={8}
                helperText={`${trainingDataForm.page_content?.length || 0} characters`} />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="AI Summary" placeholder="A concise summary..." value={trainingDataForm.summary}
                onChange={(e) => setTrainingDataForm({ ...trainingDataForm, summary: e.target.value })} multiline rows={3} />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Tags" placeholder="Comma-separated tags (e.g., safety, training)" value={trainingDataForm.tags}
                onChange={(e) => setTrainingDataForm({ ...trainingDataForm, tags: e.target.value })} helperText="Tags help categorize and improve search relevance" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setTrainingDataDialog({ open: false, content: null })}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleUpdateTrainingData} disabled={saving}>{saving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}</PrimaryButton>
        </DialogActions>
      </StyledDialog>

      {/* Training Data Edit Dialog for Supporting Documents */}
      <StyledDialog open={documentTrainingDataDialog.open} onClose={() => setDocumentTrainingDataDialog({ open: false, document: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper color="documents" sx={{ width: 44, height: 44 }}><EditIcon sx={{ fontSize: 22 }} /></IconWrapper>
            Edit Document Training Data
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Edit the extracted content, summary, and tags for this supporting document.</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Extracted Content" placeholder="The main text content..." value={documentTrainingDataForm.page_content}
                onChange={(e) => setDocumentTrainingDataForm({ ...documentTrainingDataForm, page_content: e.target.value })} multiline rows={8}
                helperText={`${documentTrainingDataForm.page_content?.length || 0} characters`} />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="AI Summary" placeholder="A concise summary..." value={documentTrainingDataForm.summary}
                onChange={(e) => setDocumentTrainingDataForm({ ...documentTrainingDataForm, summary: e.target.value })} multiline rows={3} />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Tags" placeholder="Comma-separated tags (e.g., safety, policy)" value={documentTrainingDataForm.tags}
                onChange={(e) => setDocumentTrainingDataForm({ ...documentTrainingDataForm, tags: e.target.value })} helperText="Tags help categorize and improve search relevance" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setDocumentTrainingDataDialog({ open: false, document: null })}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleUpdateDocumentTrainingData} disabled={saving}>{saving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}</PrimaryButton>
        </DialogActions>
      </StyledDialog>

      {/* Settings Dialog */}
      <StyledDialog open={settingsDialog} onClose={() => setSettingsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper color="primary" sx={{ width: 44, height: 44 }}><SettingsIcon sx={{ fontSize: 22 }} /></IconWrapper>
            Course Settings
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Course Title" value={courseSettings.title}
                onChange={(e) => setCourseSettings({ ...courseSettings, title: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Short Description" placeholder="A brief summary for course listings..." value={courseSettings.short_description}
                onChange={(e) => setCourseSettings({ ...courseSettings, short_description: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Full Description" placeholder="Detailed description of course objectives..." value={courseSettings.description}
                onChange={(e) => setCourseSettings({ ...courseSettings, description: e.target.value })} multiline rows={4} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#475569' }}>
                Course Thumbnail
              </Typography>
              <Box sx={{
                border: '2px dashed #e2e8f0',
                borderRadius: '12px',
                p: 3,
                textAlign: 'center',
                bgcolor: '#f8fafc',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: '#6366f1', bgcolor: '#f1f5f9' }
              }}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => handleThumbnailSelect(e.target.files[0])}
                  style={{ display: 'none' }}
                  id="thumbnail-upload-input"
                />
                <label htmlFor="thumbnail-upload-input" style={{ cursor: 'pointer', display: 'block' }}>
                  {(thumbnailPreview || courseSettings.thumbnail_url) ? (
                    <Box sx={{ position: 'relative' }}>
                      <img
                        src={thumbnailPreview || courseSettings.thumbnail_url}
                        alt="Course thumbnail"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 200,
                          borderRadius: '8px',
                          objectFit: 'cover'
                        }}
                      />
                      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#64748b' }}>
                        Click to change thumbnail
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <UploadIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Click to upload course thumbnail
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        Recommended: 1280x720px (16:9 ratio) - Max 5MB
                      </Typography>
                    </Box>
                  )}
                </label>
                {(thumbnailPreview || courseSettings.thumbnail_url) && (
                  <Button
                    size="small"
                    color="error"
                    onClick={(e) => { e.preventDefault(); handleRemoveThumbnail(); }}
                    sx={{ mt: 1 }}
                  >
                    Remove Thumbnail
                  </Button>
                )}
              </Box>
              {uploadingThumbnail && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="primary">Uploading thumbnail...</Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#6366f1' } }}>Category</InputLabel>
                <StyledSelect value={courseSettings.category} onChange={(e) => setCourseSettings({ ...courseSettings, category: e.target.value })} label="Category"
                  MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', border: '1px solid #e2e8f0', '& .MuiMenuItem-root': { color: '#1e293b', '&:hover': { bgcolor: '#f8fafc' } } } } }}>
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Safety">Safety</MenuItem>
                  <MenuItem value="Compliance">Compliance</MenuItem>
                  <MenuItem value="Technical">Technical</MenuItem>
                  <MenuItem value="Soft Skills">Soft Skills</MenuItem>
                  <MenuItem value="Leadership">Leadership</MenuItem>
                </StyledSelect>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#6366f1' } }}>Difficulty Level</InputLabel>
                <StyledSelect value={courseSettings.difficulty_level} onChange={(e) => setCourseSettings({ ...courseSettings, difficulty_level: e.target.value })} label="Difficulty Level"
                  MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', border: '1px solid #e2e8f0', '& .MuiMenuItem-root': { color: '#1e293b', '&:hover': { bgcolor: '#f8fafc' } } } } }}>
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </StyledSelect>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField fullWidth type="number" label="Estimated Duration" value={courseSettings.estimated_duration}
                onChange={(e) => setCourseSettings({ ...courseSettings, estimated_duration: parseInt(e.target.value) })}
                inputProps={{ min: 0 }} InputProps={{ endAdornment: <InputAdornment position="end" sx={{ color: '#64748b' }}>hours</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField fullWidth type="number" label="Passing Score" value={courseSettings.passing_score}
                onChange={(e) => setCourseSettings({ ...courseSettings, passing_score: parseFloat(e.target.value) })}
                inputProps={{ min: 0, max: 100 }} InputProps={{ endAdornment: <InputAdornment position="end" sx={{ color: '#64748b' }}>%</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2.5, borderRadius: '12px', background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '1px solid #a7f3d0' }}>
                <FormControlLabel control={<Switch checked={courseSettings.is_certificate_enabled}
                  onChange={(e) => setCourseSettings({ ...courseSettings, is_certificate_enabled: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#059669' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10b981' } }} />}
                  label={<Box><Typography variant="body2" sx={{ color: '#065f46', fontWeight: 600 }}>Enable Certificate Generation</Typography>
                    <Typography variant="caption" sx={{ color: '#059669' }}>Learners will receive a certificate upon course completion</Typography></Box>} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setSettingsDialog(false)}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleUpdateCourseSettings} disabled={saving}>{saving ? <CircularProgress size={20} color="inherit" /> : 'Save Settings'}</PrimaryButton>
        </DialogActions>
      </StyledDialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <MuiAlert elevation={6} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}
          sx={{ borderRadius: '12px', fontWeight: 500, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
            '&.MuiAlert-filledSuccess': { background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
            '&.MuiAlert-filledError': { background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
            '&.MuiAlert-filledInfo': { background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' },
            '&.MuiAlert-filledWarning': { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' } }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      {/* AI Instructor Configuration Dialog */}
      <StyledDialog 
        open={aiInstructorDialog} 
        onClose={() => setAiInstructorDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper color="primary" sx={{ width: 44, height: 44, background: 'linear-gradient(135deg, #fae8ff 0%, #e9d5ff 100%)' }}>
              <RobotIcon sx={{ fontSize: 22, color: '#a855f7' }} />
            </IconWrapper>
            Configure AI Instructor
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Instructor Name */}
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Instructor Name"
                placeholder="e.g., Professor Alex, Coach Sarah"
                value={aiInstructorSettings.instructor_name}
                onChange={(e) => setAiInstructorSettings(prev => ({ 
                  ...prev, 
                  instructor_name: e.target.value 
                }))}
                helperText="The name your AI instructor will use when speaking to students"
              />
            </Grid>

            {/* Voice Type Selection */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 2, fontWeight: 600 }}>
                Voice Type
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <VoiceTypeButton
                  selected={aiInstructorSettings.voice_type === 'FEMALE'}
                  voiceType="FEMALE"
                  onClick={() => setAiInstructorSettings(prev => ({ ...prev, voice_type: 'FEMALE' }))}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <FemaleIcon sx={{ fontSize: 32 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Female</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Warm & Professional</Typography>
                  </Box>
                </VoiceTypeButton>
                <VoiceTypeButton
                  selected={aiInstructorSettings.voice_type === 'MALE'}
                  voiceType="MALE"
                  onClick={() => setAiInstructorSettings(prev => ({ ...prev, voice_type: 'MALE' }))}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <MaleIcon sx={{ fontSize: 32 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Male</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Clear & Authoritative</Typography>
                  </Box>
                </VoiceTypeButton>
              </Box>
            </Grid>

            {/* Language Selection */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#a855f7' } }}>
                  Language
                </InputLabel>
                <StyledSelect
                  value={aiInstructorSettings.language}
                  onChange={(e) => setAiInstructorSettings(prev => ({ ...prev, language: e.target.value }))}
                  label="Language"
                  MenuProps={{ 
                    PaperProps: { 
                      sx: { 
                        bgcolor: '#ffffff', 
                        border: '1px solid #e2e8f0',
                        '& .MuiMenuItem-root': { 
                          color: '#1e293b', 
                          '&:hover': { bgcolor: '#f8fafc' } 
                        }
                      } 
                    } 
                  }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                  <MenuItem value="de">German</MenuItem>
                  <MenuItem value="pt">Portuguese</MenuItem>
                  <MenuItem value="it">Italian</MenuItem>
                  <MenuItem value="nl">Dutch</MenuItem>
                  <MenuItem value="pl">Polish</MenuItem>
                  <MenuItem value="ru">Russian</MenuItem>
                  <MenuItem value="ja">Japanese</MenuItem>
                  <MenuItem value="ko">Korean</MenuItem>
                  <MenuItem value="zh">Chinese</MenuItem>
                  <MenuItem value="ar">Arabic</MenuItem>
                  <MenuItem value="hi">Hindi</MenuItem>
                </StyledSelect>
              </FormControl>
            </Grid>

            {/* Enable/Disable Toggle */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '12px', 
                background: aiInstructorSettings.enabled 
                  ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                  : 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                border: aiInstructorSettings.enabled ? '1px solid #bbf7d0' : '1px solid #fecaca',
                height: '100%',
                display: 'flex',
                alignItems: 'center'
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={aiInstructorSettings.enabled}
                      onChange={(e) => setAiInstructorSettings(prev => ({ 
                        ...prev, 
                        enabled: e.target.checked 
                      }))}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { 
                          color: aiInstructorSettings.enabled ? '#10b981' : '#ef4444' 
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { 
                          backgroundColor: aiInstructorSettings.enabled ? '#34d399' : '#f87171' 
                        }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ 
                        color: aiInstructorSettings.enabled ? '#065f46' : '#991b1b', 
                        fontWeight: 600 
                      }}>
                        {aiInstructorSettings.enabled ? 'AI Instructor Active' : 'AI Instructor Disabled'}
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        color: aiInstructorSettings.enabled ? '#059669' : '#dc2626' 
                      }}>
                        {aiInstructorSettings.enabled 
                          ? 'Students will hear voice guidance' 
                          : 'No voice guidance will be provided'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>

            {/* Feature Toggles */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 2, fontWeight: 600 }}>
                Enable Voice Guidance For
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(aiInstructorSettings.features || {}).map(([key, value]) => {
                  const info = getScriptTypeInfo(key);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: '10px', 
                        border: value ? '1px solid #c7d2fe' : '1px solid #e2e8f0',
                        background: value ? '#eef2ff' : '#fafafa',
                        transition: 'all 0.2s ease'
                      }}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={value}
                              onChange={(e) => setAiInstructorSettings(prev => ({
                                ...prev,
                                features: { ...prev.features, [key]: e.target.checked }
                              }))}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: info.color },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { 
                                  backgroundColor: info.color 
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {React.cloneElement(info.icon, { 
                                sx: { fontSize: 18, color: value ? info.color : '#94a3b8' } 
                              })}
                              <Typography variant="body2" sx={{ 
                                color: value ? '#1e293b' : '#64748b',
                                fontWeight: value ? 600 : 400
                              }}>
                                {info.label}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setAiInstructorDialog(false)}>Cancel</SecondaryButton>
          <PrimaryButton 
            onClick={handleSaveAIInstructorSettings} 
            disabled={savingAISettings}
            sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)' }}
          >
            {savingAISettings ? <CircularProgress size={20} color="inherit" /> : 'Save Configuration'}
          </PrimaryButton>
        </DialogActions>
      </StyledDialog>

      {/* Script Preview Dialog */}
      <StyledDialog 
        open={scriptPreviewDialog.open} 
        onClose={() => {
          setScriptPreviewDialog({ open: false, scriptType: null });
          setPreviewScriptData(null);
          if (audioElement) {
            audioElement.pause();
            setAudioElement(null);
          }
          setPlayingAudio(false);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconWrapper color="primary" sx={{ width: 44, height: 44, background: 'linear-gradient(135deg, #fae8ff 0%, #e9d5ff 100%)' }}>
              <VoiceIcon sx={{ fontSize: 22, color: '#a855f7' }} />
            </IconWrapper>
            Script Preview
            {scriptPreviewDialog.scriptType && (
              <Chip 
                size="small" 
                label={getScriptTypeInfo(scriptPreviewDialog.scriptType).label}
                sx={{ 
                  bgcolor: `${getScriptTypeInfo(scriptPreviewDialog.scriptType).color}15`,
                  color: getScriptTypeInfo(scriptPreviewDialog.scriptType).color,
                  fontWeight: 600
                }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewingScript ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#a855f7', mb: 2 }} />
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Generating script preview...
              </Typography>
            </Box>
          ) : previewScriptData ? (
            <Box>
              {/* Audio Preview Box */}
              <AudioPreviewBox sx={{ mb: 3 }}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box sx={{ 
                      width: 56, height: 56, borderRadius: '50%', 
                      background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 14px rgba(139, 92, 246, 0.5)'
                    }}>
                      <RobotIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fff' }}>
                        {aiInstructorSettings.instructor_name || 'AI Instructor'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {aiInstructorSettings.voice_type === 'FEMALE' ? 'Female' : 'Male'} Voice • {aiInstructorSettings.language.toUpperCase()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Waveform Visualization */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '3px',
                    mb: 3,
                    height: 60
                  }}>
                    {[...Array(40)].map((_, i) => (
                      <WaveformBar 
                        key={i} 
                        height={Math.random() * 40 + 10}
                        isPlaying={playingAudio}
                        sx={{ animationDelay: `${i * 0.05}s` }}
                      />
                    ))}
                  </Box>

                  {/* Play Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={playingAudio ? <StopIcon /> : <PlayIcon />}
                      onClick={handlePlayAudio}
                      disabled={!previewScriptData.audio_base64}
                      sx={{
                        background: playingAudio 
                          ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                          : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        borderRadius: '50px',
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: playingAudio 
                          ? '0 4px 14px rgba(239, 68, 68, 0.4)'
                          : '0 4px 14px rgba(16, 185, 129, 0.4)',
                        '&:hover': {
                          background: playingAudio 
                            ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                            : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        },
                        '&:disabled': {
                          background: '#4c1d95',
                          color: 'rgba(255,255,255,0.5)'
                        }
                      }}
                    >
                      {playingAudio ? 'Stop' : previewScriptData.audio_base64 ? 'Play Audio' : 'No Audio'}
                    </Button>
                  </Box>
                </Box>
              </AudioPreviewBox>

              {/* Script Text */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1.5, fontWeight: 600 }}>
                  Script Text
                </Typography>
                <ScriptPreviewBubble>
                  <Typography variant="body1" sx={{ color: '#1e293b', lineHeight: 1.7 }}>
                    {previewScriptData.script_text}
                  </Typography>
                </ScriptPreviewBubble>
              </Box>

              {previewScriptData.is_fallback && (
                <Box sx={{ 
                  p: 2, 
                  borderRadius: '10px', 
                  background: '#fef3c7', 
                  border: '1px solid #fcd34d',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}>
                  <WarningIcon sx={{ color: '#b45309', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: '#92400e' }}>
                    This is a fallback script. Configure your API to generate custom audio.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
              <VoiceIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="body2">No preview available</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => {
            setScriptPreviewDialog({ open: false, scriptType: null });
            setPreviewScriptData(null);
            if (audioElement) {
              audioElement.pause();
              setAudioElement(null);
            }
            setPlayingAudio(false);
          }}>
            Close
          </SecondaryButton>
          <PrimaryButton
            onClick={() => {
              setCustomScriptForm({
                script_text: previewScriptData?.script_text || '',
                generate_audio: true
              });
              setCustomScriptDialog({ open: true, scriptType: scriptPreviewDialog.scriptType });
              setScriptPreviewDialog({ open: false, scriptType: null });
            }}
            startIcon={<EditIcon />}
            sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)' }}
          >
            Customize Script
          </PrimaryButton>
        </DialogActions>
      </StyledDialog>

      {/* Custom Script Dialog */}
      <StyledDialog 
          open={customScriptDialog.open} 
          onClose={() => {
            setCustomScriptDialog({ open: false, scriptType: null });
            setCustomScriptForm({ script_text: '', generate_audio: true });
          }} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconWrapper color="primary" sx={{ width: 44, height: 44, background: 'linear-gradient(135deg, #fae8ff 0%, #e9d5ff 100%)' }}>
                <EditIcon sx={{ fontSize: 22, color: '#a855f7' }} />
              </IconWrapper>
              Customize Script
              {customScriptDialog.scriptType && (
                <Chip 
                  size="small" 
                  label={getScriptTypeInfo(customScriptDialog.scriptType).label}
                  sx={{ 
                    bgcolor: `${getScriptTypeInfo(customScriptDialog.scriptType).color}15`,
                    color: getScriptTypeInfo(customScriptDialog.scriptType).color,
                    fontWeight: 600
                  }}
                />
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
              Write a custom script for your AI instructor. Use placeholders like {'{student_name}'}, {'{course_title}'}, {'{score}'} for personalization.
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Script Text"
                  placeholder="Write what your AI instructor should say..."
                  value={customScriptForm.script_text}
                  onChange={(e) => setCustomScriptForm(prev => ({ ...prev, script_text: e.target.value }))}
                  multiline
                  rows={6}
                  helperText={`${customScriptForm.script_text.length} characters`}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: '10px', 
                  background: '#f8fafc', 
                  border: '1px solid #e2e8f0' 
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1.5, fontWeight: 600 }}>
                    Available Placeholders
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['{student_name}', '{course_title}', '{module_title}', '{score}', '{passing_score}', '{question_count}'].map((placeholder) => (
                      <Chip
                        key={placeholder}
                        label={placeholder}
                        size="small"
                        onClick={() => {
                          setCustomScriptForm(prev => ({
                            ...prev,
                            script_text: prev.script_text + ' ' + placeholder
                          }));
                        }}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: '#eef2ff',
                          color: '#4f46e5',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          '&:hover': { bgcolor: '#e0e7ff' }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={customScriptForm.generate_audio}
                      onChange={(e) => setCustomScriptForm(prev => ({ 
                        ...prev, 
                        generate_audio: e.target.checked 
                      }))}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#a855f7' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#c084fc' }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 500 }}>
                        Generate Audio
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Create voice audio for this script
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <SecondaryButton onClick={() => {
              setCustomScriptDialog({ open: false, scriptType: null });
              setCustomScriptForm({ script_text: '', generate_audio: true });
            }}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleSaveCustomScript}
              disabled={savingAISettings || !customScriptForm.script_text.trim()}
              sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)' }}
            >
              {savingAISettings ? <CircularProgress size={20} color="inherit" /> : 'Save Custom Script'}
            </PrimaryButton>
          </DialogActions>
        </StyledDialog>

        <StyledDialog 
          open={validationDialog.open} 
          onClose={() => setValidationDialog({ open: false, errors: [], type: '', onConfirm: null })} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconWrapper 
                color={validationDialog.errors.some(e => e.type === 'error') ? 'warning' : 'primary'} 
                sx={{ width: 44, height: 44 }}
              >
                {validationDialog.errors.some(e => e.type === 'error') ? (
                  <ErrorIcon sx={{ fontSize: 22, color: '#dc2626' }} />
                ) : (
                  <WarningIcon sx={{ fontSize: 22, color: '#f59e0b' }} />
                )}
              </IconWrapper>
              {validationDialog.errors.some(e => e.type === 'error') 
                ? `Cannot Publish ${validationDialog.type === 'course' ? 'Course' : 'Module'}`
                : `Publishing ${validationDialog.type === 'course' ? 'Course' : 'Module'} - Please Review`
              }
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
              {validationDialog.errors.some(e => e.type === 'error')
                ? 'The following issues must be resolved before publishing:'
                : 'The following warnings were found. You can still proceed, but please review:'
              }
            </Typography>
            
            <Stack spacing={2}>
              {validationDialog.errors.map((error, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    p: 2, 
                    borderRadius: '10px',
                    background: error.type === 'error' 
                      ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
                      : error.type === 'warning'
                      ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'
                      : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    border: error.type === 'error' 
                      ? '1px solid #fecaca'
                      : error.type === 'warning'
                      ? '1px solid #fde68a'
                      : '1px solid #bfdbfe',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    {error.type === 'error' ? (
                      <ErrorIcon sx={{ color: '#dc2626', fontSize: 20, mt: 0.3 }} />
                    ) : error.type === 'warning' ? (
                      <WarningIcon sx={{ color: '#f59e0b', fontSize: 20, mt: 0.3 }} />
                    ) : (
                      <InfoIcon sx={{ color: '#3b82f6', fontSize: 20, mt: 0.3 }} />
                    )}
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: error.type === 'error' ? '#991b1b' : error.type === 'warning' ? '#92400e' : '#1e40af',
                          fontWeight: 600 
                        }}
                      >
                        {error.message}
                      </Typography>
                      {error.suggestion && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: error.type === 'error' ? '#dc2626' : error.type === 'warning' ? '#b45309' : '#2563eb',
                            display: 'block',
                            mt: 0.5
                          }}
                        >
                          💡 {error.suggestion}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <SecondaryButton onClick={() => setValidationDialog({ open: false, errors: [], type: '', onConfirm: null })}>
              {validationDialog.onConfirm ? 'Cancel' : 'Close'}
            </SecondaryButton>
            {validationDialog.onConfirm && (
              <PrimaryButton onClick={validationDialog.onConfirm}>
                Publish Anyway
              </PrimaryButton>
            )}
          </DialogActions>
        </StyledDialog>


    </PageContainer>
  );
};

export default CourseBuilder;